// External Dependencies
import React, { Fragment } from 'react';
import Amplify from 'aws-amplify';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import awsmobile from '../aws-exports.js';
import cx from 'classnames';
import PropTypes from 'prop-types';

// Internal Dependencies
import ReaderManager from './Reader/ReaderManager';
import StudentHome from './Student/StudentHome';
import AssessmentLanguagePicker from './Student/AssessmentLanguagePicker';
import Speech from './Reader/Speech/Speech';
import Coach from './Coach/Coach';
import ThinkingBubble from './Coach/ThinkingBubble/ThinkingBubble';
import Navigation from './Navigation';
import Menu from './Menu';
import {
  getPaginatedStory,
  getSquashedStory,
  getAssessmentStory,
  getUserData,
  setUserData,
  hasAnyCompletedActivity,
  processAssessmentStory
} from '../services/API';
import studentRecordService from '../services/StudentRecordService';
import { engagementClock } from '../services/engagementClock';
import Modal from './Modal';
import NativeReports from './Student/NativeReports/NativeReports';
import { RouteComponentRenderer, routeStore } from '../routes';
import config from '../config';
import {assignmentTypes,userTypes} from '../constants/constants';
import {COPY_STRINGS, setCopyStrings} from '../constants/translatedCopy';
import { chatbotName } from './ChatBot/ChatBot';
import SpeechButton from './SpeechButton';
import {ErrorModal, getErrorModalTypes} from './Reader/ErrorModal';
import StudentHistory from './Student/StudentHistory';
import TutorPicker from './Student/TutorPicker/TutorPicker.js';
import HelpButton from './Teacher/HelpButton';
import Legend from './Teacher/Legend';
import {legendItems, dyslexiaRiskLegendItems} from '../constants/legend';
import silence from "../images/silence.mp3";
import {isMicAllowed} from "../services/environment"
import assessmentInProgressIcon from "../images/icons/ISIPinProgress_topRight.svg";
import device from '../services/device/device.js';
import compatibilityService from '../services/compatibilityService.js';
import LanguageSettings from './Settings/LanguageSettings';
import {logSessionInfo} from '../services/logger';
import ChatSupport from "Components/Teacher/ChatSupport";
import EmailCaptureModal from "./Teacher/EmailCaptureModal";
import {getAssignmentForStudent, getAssignmentByIdForStudent, getSpanishCalibrationAssignment} from "Services/assignment/getAssignment/getAssignment";
import {getPendingManifest} from "Services/assignment/functions";
import {getAssignmentConfiguration, getEnglishOnlyAssessmentConfig} from "Services/assignment/manifest.js";
import { isBTSEffective } from "Services/entitlementService";
import VirtualScreen from './Reader/VirtualScreen/VirtualScreen.js';
import {ASSIGNMENT_SEQUENCE, ASSIGNMENT_TYPE} from "Services/assignment/constants";
import Loading from "./Global/Loading";
import {StudentPreChecks, READY_MODULE_TEST_MODES, StudentASRReaderWrapper} from './StudentPreChecks/index';
import StudentInformationService from '../services/StudentInformationService.js';
import { getCalibrationAssignment, getScreenerOverrides } from '../services/calibrationForms.js';
import {fetchBadges} from '../services/badging/fetchBadges/fetchBadges';
import { getLanguageForLocale, formatLocale_LowerCaseHyphen } from '../services/locale.js';
import { CurrentStoryWordMetadataProvider } from '../context/CurrentStoryMetadataContext.js';
import { GetBrandedAppName, GetBrandedTutorName, GetBrandedProhibitTutorSelection } from '../services/BrandingService.js';
import ReaderManagerErrorBoundary from './Reader/ReaderManagerErrorBoundary.js';
import { isAssessmentTypeAssignment, shouldUseAOS as shouldUseAOSForAssignment, shouldShowAssessmentInProgressIcon } from '../services/util.js';
import {
  applyStickyMockAosFromInitialParams,
  clearDevStickyMockAosState,
  isMockAOSNow,
} from '../services/AssessmentOrchestrationService';
import { FEATURE_FLAGS } from '../constants/featureFlags.js';
import {
  ASR_PRELOAD_FAILURE_REASON,
  ensureAsrAssetsReady,
  FETCH_TIMEOUT_MS,
  getStorageDiagnostics,
  isAsrCacheSupported,
  requestPersistentStorage,
} from '../services/asr/asrAssetCache';
import { getActiveAsrManifest, probeLocalAsrModelPresence } from '../services/asr/asrBackend';
import { planLocalAsrBoot, boundBootTimeoutToGateBudget, estimatedBootFraction, compositeAsrLoadProgress } from '../services/asr/localAsrBoot';
import { ensureLocalPlacementAsrBooted, disposeLocalPlacementAsr } from '../services/SpeechStream';
import { isLocalAsrEnabled, isLocalAsrAssessmentSession, isLocalAsrSessionModeResolved, setLocalAsrAssessmentSession, explainLocalAsrEnablement } from '../services/asr/localAsrEnablement';
import { recordLocalAsrLifecycle, recordLocalAsrFailure, flushLocalAsrLifecycle } from '../services/asr/localAsrLifecycle';
import { recordLocalAsrDeviceProfile } from '../services/asr/deviceProfile';
import calendarService from '../services/CalendarService.js';
import amiraAssignmentWrapper from '../services/assignment/amiraAssignmentService.js';
import { getPendingTutorManifest } from '../services/assignment/functions/getPendingManifest.js';
import StudentHomeSettingsModal from './Teacher/StudentHomeSettingsModal.js';
import MutedTroubleshootModal from './StudentPreChecks/Components/MutedTroubleshootModal.js';
import { getCopyByGradeband } from '../services/ThemingService.js';
import { withTimeout } from '../services/util.js';

//Note - this is meant to be persistent throughout the entire
// login session so do NOT clear it ever.
let activityStack = [];
let activityObjectStack = [];

const ASR_PROGRESS_UPDATE_INTERVAL_MS = 100;

/** How often the stall watchdog samples download progress. Distinct from
 *  DRAIN_STALL_TIMEOUT_MS in asrAssetCache (per-chunk body timeout, same 30 s
 *  value, different purpose — that one resets on each chunk, this one checks
 *  aggregate progress). The watchdog also defers to the cache layer's own
 *  FETCH_TIMEOUT_MS before declaring a stall (TT-1268). */
const ASR_STALL_CHECK_INTERVAL_MS = 30000;

/** How often the estimated boot slice of the loading bar re-renders (TT-1313). The
 *  boot posts no real progress, so this is purely the animation cadence. */
const ASR_BOOT_PROGRESS_TICK_MS = 250;
/** Below this much fetched data the precache download is not a usable link-speed
 *  sample for the cold-boot estimate (a fully warm Cache Storage fetches ~nothing). */
const ASR_THROUGHPUT_MIN_SAMPLE_BYTES = 262144;
const ASR_THROUGHPUT_MIN_SAMPLE_MS = 500;
// The school-level products amira-educators-client's NO_SUBSCRIPTIONS block checks (ASSMNT-2567).
// Keep in step with the `subscriptions { ... }` selection in GQL_GET_LICENSING (StudentRecordService.js):
// a product added there and missing here would leave that school blocked.
const SCHOOL_PRODUCT_KEYS = ['assess', 'instruct', 'tutor', 'evaluar', 'ensenar', 'tutora'];

// Strip microLesson / istationLegacyActivity entries (both top-level manifest items and
// nested activities) from an assignment's manifest, mutating it in place. The default
// assessment launch applies this to the primary assignment; the BTS subject-resolved
// assignments must go through the same filter so they can't launch activity types the
// default path would have removed.
function stripInvalidManifestActivities(assignment) {
  if (!assignment || !assignment.manifest) {
    return;
  }
  assignment.manifest = assignment.manifest.filter(item => {
    if (item.activityType === 'microLesson' || item.activityType === 'istationLegacyActivity') {
      return false;
    }
    if (item.activity && Array.isArray(item.activity)) {
      item.activity = item.activity.filter(activity =>
        activity.type !== 'microLesson' && activity.type !== 'istationLegacyActivity'
      );
    }
    return true;
  });
}

export function getActivityStack(){
  return activityStack;
}

export function getActivityObjectStack(){
  return activityObjectStack;
}

export function addToActivityStack(activity){
  activityStack.push(activity);
}

export function removeLastActivityFromStack(){
  return activityStack.pop();
}

// Configure global AWS SDK settings BEFORE Amplify.configure
// This ensures all AWS clients (including CognitoIdentity) inherit these settings
if (typeof AWS !== 'undefined' && AWS.config) {
  AWS.config.maxRetries = 1; // Override automatic retries for ALL AWS SDK clients
}

Amplify.configure(Object.assign({}, awsmobile, {
  Interactions: {
    bots: {
      [chatbotName]: {
        name: chatbotName,
        alias: "$LATEST",
        region: "us-east-1",
      },
    }
  },
  // Configure AWS SDK retry behavior for Cognito Identity
  AWS: {
    maxRetries: 1, // Override automatic retries for Cognito Identity calls
  },
  Auth: {
    'authenticationFlowType': 'USER_PASSWORD_AUTH',
  }
}));

class TeacherRoutes extends React.Component {
  render() {
    //These routes are only available to teachers
    if(this.props.userType == userTypes.TEACHER){
      return (
        <Switch>
          {routeStore.teacher.routes.map((routeConfig) =>
            <RouteComponentRenderer key={routeConfig.path} {...routeConfig} {...this.props} />
          )}
          <Route render={(props) => <Redirect to={routeStore.teacher.DEFAULT} {...props} />} />
        </Switch>
      );
    }
    return null;
  }
}

class SchoolAdminRoutes extends React.Component {
  render() {
    // These routes are only available to admins
    // Note that admins currently share identical routes
    if(this.props.userType == userTypes.SCHOOL_ADMIN){
      return (
        <Switch>
          {routeStore.SCHOOL_ADMIN.routes.map((routeConfig) =>
            <RouteComponentRenderer key={routeConfig.path} {...routeConfig} {...this.props} />
          )}
          <Route render={(props) => <Redirect to={routeStore.SCHOOL_ADMIN.DEFAULT} {...props} />} />
        </Switch>
      );
    }
    return null;
  }
}

class DistrictAdminRoutes extends React.Component {
  render() {
    // These routes are only available to admins
    // Note that admins currently share identical routes
    if(this.props.userType == userTypes.DISTRICT_ADMIN){
      return (
        <Switch>
          {routeStore.DISTRICT_ADMIN.routes.map((routeConfig) =>
            <RouteComponentRenderer key={routeConfig.path} {...routeConfig} {...this.props} />
          )}
          <Route render={(props) => <Redirect to={routeStore.DISTRICT_ADMIN.DEFAULT} {...props} />} />
        </Switch>
      );
    }
    return null;
  }
}

class StudentRoutes extends React.Component {
  render() {
    if(this.props.userType == userTypes.STUDENT && !this.props.forcedAssessment){
      return (
        <Switch>
          {this.props.children}
        </Switch>
      );
    }
    return null;
  }
}

class ForcedAssessment extends React.Component {
  render() {
    if(this.props.userType == userTypes.STUDENT && this.props.forcedAssessment){
      return (
        <Switch>
          {this.props.children}
        </Switch>
      );
    }
    return null;
  }
}

export class App extends React.Component {
  constructor (props){
    super(props);
    this.currentPathname = null;
    this.currentSearch = null;

    this.studentPreCheckLogEventRef = null;
    this.asrPreloadToken = 0;
    // TT-1313 gated boot bookkeeping (see bootLocalAsrForActivePreload).
    this.asrBootTicker = null;
    this.asrBootTimeout = null;
    this.asrBootInFlight = false;
    this.asrFirstFetchActivityAt = 0;
    this.asrInitialProgressFraction = null;
    this.asrLatestProgressFraction = 0;
    this.asrDownloadSettledAt = 0;
    this.asrPreloadStartedAt = 0;
    this.asrPreloadPromise = null;
    // Cancels the active preload's in-flight fetches when a retry/abandon/stall
    // supersedes it (TT-1267), so an abandoned attempt stops downloading rather than
    // racing the new one for the same ~22.8MB acoustic model. Paired with the token
    // bump, which no-ops the abandoned attempt's settle path.
    this.asrPreloadAbortController = null;
    this.asrPersistPromise = null;
    this.asrProgressTimeout = null;
    this.asrPendingProgress = null;
    this.lastAsrProgressUpdate = 0;
    this.asrStallWatchdogTimeout = null;
    this.asrLastWatchdogProgress = -1;
    this.asrLastFetchActivityAt = 0;
    this.asrHighWaterProgress = 0;
    this.asrPreloadFailureReason = null;
    // TT-1266: whether the user has already used their one manual retry of the
    // ASR asset download. After this, any subsequent failure degrades to server
    // ASR instead of re-showing the retry modal.
    this.asrHasRetried = false;
    this.handleOnline = this.handleOnline.bind(this);

    // Capture initial URL params at app startup for session restart (used by mid-session mic troubleshoot)
    // URL params may get modified during the app lifecycle, so capture them early
    const urlParams = new URLSearchParams(window.location.search);
    this.initialUrlParams = {};
    for (const [key, value] of urlParams.entries()) {
      this.initialUrlParams[key] = value;
    }
    // Same sticky cache as restartSession / ReaderManager — mock AOS reads mockAOS/mockSuite/mockIndex after query string is gone
    applyStickyMockAosFromInitialParams(this.initialUrlParams);

    // TT-828: local ASR is tutor-only. Launchers that carry an `assignmentType` URL
    // param classify the session before anything below reads isLocalAsrEnabled(), so an
    // assessment never even starts the model download. Entries without the param stay
    // unresolved until checkAssignments answers it.
    if (this.props.assignmentType) {
      setLocalAsrAssessmentSession(isAssessmentTypeAssignment(this.props.assignmentType));
    }

    // Define routes that should hide the coach
    this.routesThatHideCoach = ['/student/tutorPicker'];

    this.state = {
      // user
      paginatedStory: null,
      pageIndex: 0,
      assignmentIndex: 0,
      activityIndex: 0, // a count of activity during an app session, used for presenting introductions between full and short
      coachMessage: {id: null, text: "Let's get started!", texttoread: null},
      interventions: {},    // an object containing our intervention functions, will be set via callback by Reader
                            // TODO: eventually we may want to add interventions from multiple parts of the app, not just reader. When that time comes, adjust this structure accordingly
      coachReady: getUserData().sessionData.isiPad || this.props.noAvatar, // if we're on iPad we're not going to load the coach, so we don't have to wait for it to be ready
      modal: null,
      audioInputErrorModal: null, // Separate modal for mic muted errors to prevent interference
      loading: true,
      // TT-825: gates leaving the "Loading..." state on local ASR asset pre-cache.
      // Starts ready (true) unless local ASR pre-caching applies to this session
      // (feature flag + supported device + cache support + license + student), so it
      // is a no-op for teachers, unsupported devices, unsupported cache environments,
      // and flag-off sessions.
      asrAssetsReady: !(isLocalAsrEnabled() && isAsrCacheSupported() && getUserData().userType === userTypes.STUDENT),
      // TT-825: whether on-device ASR assets cached/validated successfully. Consumed by
      // the wiring ticket (TT-828) to decide local vs server placement at runtime.
      localAsrReady: false,
      // TT-825: 0..1 progress of the ASR asset download, surfaced on the loading screen.
      asrDownloadProgress: 0,
      // TT-1313: 0..1 ESTIMATED progress of the gated engine boot that follows the download.
      // The two are composed into one bar by getAsrLoadProgressPercent().
      asrBootProgress: 0,
      // TT-825: true when the ASR asset download failed after retries; shows retry UI.
      asrDownloadError: false,
      preChecksDone: getUserData().userType === userTypes.STUDENT ? false : true,
      ready: this.shouldByPassStudentSetup(),
      initialUserInteraction: false,
      isReaderActive: getUserData().userType == userTypes.STUDENT ? true : false,//ChatBot will only  be available for Teachers
      forcedAssessment: null,
      forceStory: props.forceStory,
      orfMode: true,  //TODO: refactor when assignments carry the assignmentType information, and when ORF uses the same technique as other assignments
      error: false,
      readerKey: 0,
      midAssessment: false,   //whether or not we are currently in the middle of a not-yet-complete assessment
      micEnabled: false,
      micChecked: false,
      hideReaderContainer: true,
      readyModuleTestMode: this.getReadyModuleTestModes(),
      forcedPhrasesPerPage: this.getForcedPhrasesPerPage(),
      animationToPlay: null,
      readyModal: false,
      forceSkill: props.skillId,
      period: null,
      schoolYear: null,
      studentHomeSettingsVisible: false,
      teacherMicTest: props.teacherMicTest,
      teacherMicTestMode: null,
      isNonVerbalAssignment: false,
      midSessionMicTroubleshoot: false,
      audioIssueType: {},
      // BTS bilingual chooser: the two locale-specific assessments to choose between,
      // stashed at assignment-resolution time and consumed after the "Let's do it" click.
      btsBilingualChoice: null,
    };

    this.nextPage = this.nextPage.bind(this);
    this.previousPage = this.previousPage.bind(this);
    this.setAssessmentStoryDirectly = this.setAssessmentStoryDirectly.bind(this);
    this.setStory = this.setStory.bind(this);
    this.resetStory = this.resetStory.bind(this);
    this.resetForcedStory = this.resetForcedStory.bind(this);
    this.setCoachMessage = this.setCoachMessage.bind(this);
    this.setInterventions = this.setInterventions.bind(this);
    this.coachReadyCallback = this.coachReadyCallback.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.muteReader = this.muteReader.bind(this);
    this.unMuteReader = this.unMuteReader.bind(this);
    this.clearAssignment = this.clearAssignment.bind(this);
    this.setMidAssessment = this.setMidAssessment.bind(this);
    this.logTimeOnStoryStart = this.logTimeOnStoryStart.bind(this);
    this.logTimeOnStoryEnd = this.logTimeOnStoryEnd.bind(this);
    this.saveSessionTime = this.saveSessionTime.bind(this);
    this.checkAssignments = this.checkAssignments.bind(this);
    this.preloadLocalAsrAssets = this.preloadLocalAsrAssets.bind(this);
    this.retryLocalAsrDownload = this.retryLocalAsrDownload.bind(this);
    this.canSessionLaunchAssessment = this.canSessionLaunchAssessment.bind(this);
    this.resolveLocalAsrSessionMode = this.resolveLocalAsrSessionMode.bind(this);
    this.abandonLocalAsrPreload = this.abandonLocalAsrPreload.bind(this);
    this.canSessionAccessAmira = this.canSessionAccessAmira.bind(this);
    this.refreshAssignments = this.refreshAssignments.bind(this);
    this.handleStudentInboxTutor = this.handleStudentInboxTutor.bind(this);
    this.nextAssignment = this.nextAssignment.bind(this);
    this.forceAssignment = this.forceAssignment.bind(this);
    this.setParentActivityId = this.setParentActivityId.bind(this);
    this.openReadyModal = this.openReadyModal.bind(this);
    this.onPreCheckDone = this.onPreCheckDone.bind(this);
    this.toggleReaderContainer = this.toggleReaderContainer.bind(this);
    this.onStartButtonClicked = this.onStartButtonClicked.bind(this);
    this.onDonePlayingBootstrap = this.onDonePlayingBootstrap.bind(this);
    this.openBtsLanguageChooser = this.openBtsLanguageChooser.bind(this);
    this.stageElement = React.createRef(); // Reference to the stage element added to pass pixi app down to subcomponents
    this.readerManagerRef = React.createRef(); // Reference to ReaderManager methods for ASR checks
    this.switchReaderContainerBackground = this.switchReaderContainerBackground.bind(this);
    this.shouldExitAfterAOS = this.shouldExitAfterAOS.bind(this);
    this.showTutorPickerButton = this.showTutorPickerButton.bind(this);
    this.hideTutorPickerButton = this.hideTutorPickerButton.bind(this);
    this.navigateToTutorPicker = this.navigateToTutorPicker.bind(this);
    this.openStudentHomeSettingsModal = this.openStudentHomeSettingsModal.bind(this);
    this.getLastEnglishAssignmentIndex = this.getLastEnglishAssignmentIndex.bind(this);
    this.setNextStory = this.setNextStory.bind(this);
    this.skipNextReadingComprehension = this.skipNextReadingComprehension.bind(this);
    this.resetTeacherMicTestMode = this.resetTeacherMicTestMode.bind(this);

    // Mic muted helper methods
    this.showMicMutedTroubleshootModal = this.showMicMutedTroubleshootModal.bind(this);
    this.setLogEventCallback = this.setLogEventCallback.bind(this);
    this.handleRestart = this.handleRestart.bind(this);
    
    // Mid-session mic troubleshoot methods
    this.startMidSessionTroubleshoot = this.startMidSessionTroubleshoot.bind(this);
    this.restartSession = this.restartSession.bind(this);
    this.restartSessionSuppressingTroubleshoot = this.restartSessionSuppressingTroubleshoot.bind(this);

    //TODO: This shouldn't be necessary after integrating with Router
    this.useNav = this.props.config.useNav;
    this.useMenu = this.props.config.useMenu;

    const session = getUserData();
    const isStudent = session.userType == userTypes.STUDENT
    this.useCoach = !this.props.noAvatar && isStudent

    this.didGetAssignmentForStudent = false
    this.shouldSkipNextReadingComp = false

    this.totalReadingTime = {
      start: null,
      end: null,
      activeTime: undefined, // GT-327: engagementClock.stop() result, written by saveSessionTime
    };

    this.setBadges();

    props.setPreChecksDone(this.state.preChecksDone);
  }

  componentDidMount(){
    // Check for WebGL initialization error - log it but don't block the user
    if(this.props.webglError){
      console.warn('WebGL initialization failed, falling back to static avatar');
      // Don't block the user - let the app continue with static fallback
    }

    const { history } = this.props;

    // Eat the back button (source: https://subwaymatch.medium.com/disabling-back-button-in-react-with-react-router-v5-34bb316c99d7)
    history.listen((newLocation, action) => {
      if(getUserData().userType !== userTypes.STUDENT) return;

      if (["PUSH","REPLACE"].includes(action)) {
        if (
          newLocation.pathname !== this.currentPathname ||
          newLocation.search !== this.currentSearch
        ) {
          // Save new location
          this.currentPathname = newLocation.pathname;
          this.currentSearch = newLocation.search;

          // Clone location object and push it to history
          history.push({
            pathname: newLocation.pathname,
            search: newLocation.search
          });
        }
      } else {
        // Send user back if they try to navigate back
        history.go(1);
      }
    }).bind(this);

    let session = getUserData();
    //set the locale (Spanish pilot URL flag uses Spanish copy for Ready Module etc.)
    setCopyStrings(this.props.isSpanishPilot ? 'es-mx' : session.locale);

    // check compatibility (skip if ignoreBrowserCompatibilityCheck license is enabled)
    if(device.browser && !session.ignoreBrowserCompatibilityCheck){
      console.log("Browser:", device.getBrowserForSession());
      logSessionInfo("device_information",{
        windowSize: {
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio
        },
        userAgent: window.navigator.userAgent,
        deviceInfo: device
      });

      if(device.isMobilePlatform){
        //###Leave this commented out for now, but restore once intermittent bug is resolved
        /*this.setState({error: true, ready: false});
        this.openModal(
          <ErrorModal
            type={getErrorModalTypes().COMPAT_MOBILE}
          />
        );
        return;*/
        logSessionInfo("incompatible_mobileplatform_detected",{userAgent: window.navigator.userAgent,deviceInfo: device});
      }
      if(!device.hasCompatibleOS){
        this.setState({error: true, ready: false});
        this.openModal(
          <ErrorModal
            type={getErrorModalTypes().COMPAT_OS}
            returnUrl={this.props.returnUrl}
          />
        );
        logSessionInfo("incompatible_os_detected",{userAgent: window.navigator.userAgent,deviceInfo: device});
        return;
      }
      if(!compatibilityService.checkBrowser()){
        this.setState({error: true, ready: false});
        this.openModal(
          <ErrorModal
            type={getUserData().sessionData.isiPad ? getErrorModalTypes().COMPAT_IPAD : getErrorModalTypes().COMPAT}
            returnUrl={this.props.returnUrl}
          />
        );
        logSessionInfo("incompatible_browser_detected",{userAgent: window.navigator.userAgent,deviceInfo: device});
        return;
      }
    }
    if(getUserData().sessionData.isiPad && getUserData().userType == userTypes.TEACHER){
      this.setState({error: true, ready: false});
      this.openModal(
        <ErrorModal
          type={getErrorModalTypes().IPAD}
          returnUrl={this.props.returnUrl}
        />
      );
      return;
    }

    // prevent deep link to activity page
    if(location.pathname.includes("scoring")) {
      this.props.history.push("/teacher");
    }

    this.setState({
      readyModal: true,
    }) // open ready modal, usually in loading state.

    if(session.userType === userTypes.STUDENT) {
      if (!this.canSessionAccessAmira(session)) {
        // Campus Product Access has every product disabled for this student (ASSMNT-2567 /
        // CR-10770). Show the same compat-error modal pattern used above instead of bouncing
        // silently, so the student gets an explanation instead of a dead end or an unexplained
        // re-login loop.
        // readyModal was just set true above; clear it here too so openReadyModal's document.title
        // side effect doesn't run behind this modal on every subsequent render.
        this.setState({error: true, ready: false, readyModal: false});
        this.openModal(
          <ErrorModal
            type={getErrorModalTypes().PRODUCT_ACCESS}
            returnUrl={this.props.returnUrl}
          />
        );
        logSessionInfo("product_access_disabled", {
          userId: session.userId,
          schoolId: session.schoolId,
          districtId: session.districtId,
          hasReturnUrl: Boolean(this.props.returnUrl),
          subscriptions: session.subscriptions
        });
        return;
      }

      // TT-825/TT-828: pre-cache on-device ASR assets in parallel with assignment
      // loading so the model is ready before "Let's do it"; failures keep the ready gate
      // closed and surface retry UI until TT-831 adds runtime fallback semantics.
      // Local ASR is tutor-only, so a session that could still turn out to be an
      // assessment holds the download until checkAssignments classifies it. Sessions
      // that cannot launch an assessment at all are classified now, which keeps the
      // original overlap between the download and assignment loading.
      if (this.canSessionLaunchAssessment(session)) {
        this.preloadLocalAsrAssets();
      } else {
        this.resolveLocalAsrSessionMode(false);
      }

      if (
        this.props.fromStudentInbox &&
        (
          !this.props.assignmentType ||
          !isAssessmentTypeAssignment(this.props.assignmentType)
        )
      ) {
        // If we are coming from student inbox AND
        // There is no assignmentType OR the assignmentType is not an assessment type
        // then we should move straight into the tutor experience
        this.handleStudentInboxTutor(session);
      } else {
        // For all other cases, proceed with assessment flow
        this.checkAssignments(session);
      }
    }
    window.addEventListener('recorderTrackMuted', this.showMicMutedTroubleshootModal);
    window.addEventListener('online', this.handleOnline);
  }

  componentWillUnmount() {
    this.asrPreloadToken += 1;
    // Cancel the active preload's in-flight fetches on unmount (TT-1267) — otherwise a
    // dev HMR remount recreates the double-download this ticket exists to prevent.
    this.abortActiveAsrPreload();
    this.asrPreloadPromise = null;
    if (this.asrProgressTimeout) {
      clearTimeout(this.asrProgressTimeout);
      this.asrProgressTimeout = null;
    }
    if (this.asrStallWatchdogTimeout) {
      clearTimeout(this.asrStallWatchdogTimeout);
      this.asrStallWatchdogTimeout = null;
    }
    if (this.asrBootInFlight) {
      // AppContainer renders null when auth state drops, so a logout or session expiry
      // mid-boot lands here in production. Record the abandon like abandonLocalAsrPreload
      // does, or the analyzer counts this teardown as a mid-boot death (PR #6006 bugbot).
      this.recordLocalAsrBootAbandoned();
      this.asrBootInFlight = false;
      disposeLocalPlacementAsr();
    }
    this.stopAsrBoot();
    window.removeEventListener('recorderTrackMuted', this.showMicMutedTroubleshootModal);
    window.removeEventListener('online', this.handleOnline);
    this.studentPreCheckLogEventRef = null;
  }

  componentDidUpdate(previousProps, previousStates){
    if(this.state.preChecksDone && !this.state.ready){
      this.setState({
        ready: true
      })
    }
    
  }


  getReadyModuleTestModes(){
    const urlParams = new URLSearchParams(window.location.search);
    const teacherMicTest = this.props.teacherMicTest;
    const mode = this.props.skipIntros ? READY_MODULE_TEST_MODES.SKIP : urlParams.get('readyModuleTestMode')

    if (this.props.goToTutorPicker || this.props.goToProgressPage) {
      return READY_MODULE_TEST_MODES.SKIP
    }

    // If teacherMicTest is true, override the mode to RETURNED_USER_WITH_ASSESSMENTS
    if (teacherMicTest) {
      return READY_MODULE_TEST_MODES.RETURNED_USER_WITH_ASSESSMENTS
    }

    if([READY_MODULE_TEST_MODES.SKIP,
        READY_MODULE_TEST_MODES.FTUE,
        READY_MODULE_TEST_MODES.RETURNED_USER_WITH_ASSESSMENTS,
        READY_MODULE_TEST_MODES.RETURNED_USER,
        READY_MODULE_TEST_MODES.ASR_ONLY].includes(mode)){
      return mode
    }
    return null
  }

  getForcedPhrasesPerPage(){
    const urlParams = new URLSearchParams(window.location.search);
    const phrasesPerPage = urlParams.get('phrasesPerPage')

    if(phrasesPerPage){
      return phrasesPerPage
    }
    return null
  }

  shouldByPassStudentSetup(){
    const userData = getUserData()
    const shouldByPass = [userTypes.TEACHER, userTypes.DISTRICT_ADMIN, userTypes.SCHOOL_ADMIN].includes(userData.userType)
    return shouldByPass
  }

  setLocale(locale){
    setUserData("locale", locale);

    //set html lang attribute for accessibility
    document.documentElement.lang = getLanguageForLocale(locale);

    this.setBilingualMode();
  }

  setBilingualModeLocale(bilingualLocale){
    setUserData("bilingualLocale", bilingualLocale);
    this.setBilingualMode();
  }

  setBilingualMode(){
    const { locale, bilingualLocale } = getUserData();
    const bilingualMode = (bilingualLocale && bilingualLocale !== locale);
    setUserData("bilingualMode", bilingualMode);
    const effectiveLocale = this.props.isSpanishPilot
      ? 'es-mx'
      : (bilingualMode ? bilingualLocale : locale);
    setCopyStrings(effectiveLocale);
  }

  switchReaderContainerBackground(current, newBG) {
    if(current) document.querySelector(".readerContainer").classList.remove(current);
    if(newBG) document.querySelector(".readerContainer").classList.add(newBG);
  }

  async setBadges(){
    const session = getUserData();
    if(session.badgingEnabled && !this.props.amiraIsipPilotGuid){
      const badges = await fetchBadges([session.userId]);
      setUserData("badges", badges);
    }else{
      setUserData("badges", []);
    }
  }

  toggleReaderContainer() {
    this.setState(prevState => ({
      hideReaderContainer: !prevState.hideReaderContainer
    }));
  }

  forceAssignment(){
    //This will always force a single ORF assignment
    let locale = getUserData().isESL ? "es-mx" : "en-us";
    this.setState({
      forcedAssessment: [{
        "activityType": "assessment",
        "locale": locale,
        "status": "ASSIGNED",
        "sequence": "CONTINUE"
      }],
      orfMode: true,
      assignmentIndex: 0
    });
  }

  setParentActivityId(newParentActivityId){
    this.setState({parentActivityId: newParentActivityId});
  }


  async onStartButtonClicked(speechProps){
    // Bootstrap the app by playing a silent sound. This is to get around the
    // fact that iOS does not allow playing sounds without user interaction.
    await speechProps.playAppSoundsWithPromise({
      url: silence,
      key: 'silence',
      onfinish: this.onDonePlayingBootstrap,
    }, 2500);
  }

  onDonePlayingBootstrap(){
    this.setState({
      readyModal: false,
      initialUserInteraction: true,
    }, () => {
      // BTS bilingual: after the Ready-to-Start click, present the language chooser
      // instead of going straight to pre-checks. Pre-checks runs once a language is picked.
      if (this.state.btsBilingualChoice) {
        this.openBtsLanguageChooser();
        return;
      }
      this.props.history.push('/student/pre-checks')
    })
  }

  openBtsLanguageChooser(){
    const { enAssignment, esAssignment, enManifest, esManifest, isNonVerbalAssignment } = this.state.btsBilingualChoice;
    const session = getUserData();
    this.openModal(
      <AssessmentLanguagePicker
        studentName={session.firstName}
        onChooseLanguage={(chosenLocale) => {
          // Kept fully synchronous so every setState + the navigation batch into a
          // single commit (legacy React batches setStates inside a synthetic event
          // handler, but NOT across an await). This mirrors the normal "Let's do it"
          // hand-off: forcedAssessment is set and the URL advances to /student/pre-checks
          // in the same commit, so no intermediate render can trip the redirect guards.
          const isES = chosenLocale.toLowerCase().startsWith('es');
          const chosenAssignment = isES ? esAssignment : enAssignment;
          const chosenManifest = isES ? esManifest : enManifest;
          // Recompute the language config from the actually-chosen assignment — the config
          // set during checkAssignments was derived from the legacy `assignment`, which may
          // be the other locale. Kept synchronous so it batches with the launch below, and
          // guarded so a config-derivation throw can't strand the student with an open modal
          // and no launch/navigation.
          try {
            setUserData('assignmentConfiguration', getAssignmentConfiguration(chosenAssignment));
          } catch (error) {
            console.error('[BTS] failed to recompute assignmentConfiguration for chosen locale', error);
          }
          // GT-413: a tutorInSpanish student who picks the English assessment keeps the es-mx
          // login locale through the Ready Module (Spanish intro video and Spanish ASR check),
          // as on the inbox path. ReaderManager.selectGrade sets en-us from the manifest when
          // the first task launches, so the tasks still decode on English ASR.
          if (isES || !getUserData().tutorInSpanish) {
            this.setLocale(chosenLocale);
          }
          this.launchAssessmentManifest(chosenAssignment, chosenManifest, isNonVerbalAssignment);
          this.closeModal();
          this.setState({ btsBilingualChoice: null });
          this.props.history.push('/student/pre-checks');
        }}
      />
    );
  }

  onLanguageSettingsClose(locale){
    this.setBilingualModeLocale(locale);
    this.setState({
      readyModal: true,
    }) // open ready modal, usually in loading state.
  }

  openReadyModal(speechProps){
    document.title = `Ready to Start - ${GetBrandedAppName()}`;

    // isMicAllowed().then((isMicAllowed) => {
      // this.setState({micEnabled: isMicAllowed, micChecked: true});
      // TT-825: local ASR asset pre-cache completion is one of the gates for showing
      // "Let's do it". asrAssetsReady is true by default unless local ASR pre-caching
      // applies to this session, so this does not affect teachers or flag-off sessions.
      const readyToStart = this.state.coachReady && !this.state.loading && this.state.asrAssetsReady;
      // TT-825: while local ASR is enabled, the loading screen shows download progress
      // and, on failure, a retry control. These strings are inline pending localization
      // because the feature is initially en-us only and flag-gated (see TT-828).
      const asrProgressPercent = this.getAsrLoadProgressPercent();
      const isAsrAssetLoading = !this.state.asrAssetsReady && !this.state.asrDownloadError;
      const readyModalLabel = isAsrAssetLoading
        ? `${COPY_STRINGS.loading}...`
        : COPY_STRINGS.ready_to_start;
      let pendingContent;
      if (this.state.asrDownloadError) {
        pendingContent = (
          <div className="asrProgress">
            <span className="asrProgressError">{"We're having trouble getting things ready."}</span>
            <button className="asrRetryButton" onClick={this.retryLocalAsrDownload}>{'Try again'}</button>
          </div>
        );
      } else if (!this.state.asrAssetsReady) {
        pendingContent = (
          <div className="asrProgress">
            <div className="asrProgressLabel">
              <span>{`${asrProgressPercent}%`}</span>
            </div>
            <div className="asrProgressTrack">
              <div className="asrProgressFill" style={{ width: `${asrProgressPercent}%` }} />
            </div>
          </div>
        );
      } else {
        pendingContent = (
          <div className="startButton disabled">{COPY_STRINGS.loading} <Loading inline autoFit/></div>
        );
      }
      if(!this.state.ready && !this.state.error){
        return (
          <div className="modalBackground" tabIndex="-1">
            <div className="modalContainer" tabIndex="-1">
              <div className="readyModal">
                <span className="labelFontOverride">{readyModalLabel}</span>
                {readyToStart ?
                  <button className="startButton" onClick={() => {this.onStartButtonClicked(speechProps)}}>{COPY_STRINGS.lets_do_it}</button>
                  : pendingContent}
              </div>
            </div>
          </div>
        );
      }
    // });
  }

	getLastEnglishAssignmentIndex() {
		const { forcedAssessment } = this.state;

    if (!forcedAssessment || forcedAssessment.length === 0) {
      return -1;
    }

		// Find the last item with English locale
		let lastEnglishLocaleIndex = -1;

		for (let i = 0; i < forcedAssessment.length; i++) {
			const item = forcedAssessment[i];
			const locale = item.locale ? item.locale.toLowerCase() : 'en-us';

			if (locale === 'en-us' || locale === 'en_us') {
				lastEnglishLocaleIndex = i;
			}
		}

		return lastEnglishLocaleIndex;
	}

  shouldExitAfterAOS() {
    const { forcedAssessment } = this.state;

    if (!forcedAssessment || forcedAssessment.length === 0) {
      return true;
    }

    const lastEnglishLocaleIndex = this.getLastEnglishAssignmentIndex();

    // If no English locale item found, default to true
    if (lastEnglishLocaleIndex === -1) {
      return true;
    }

    const lastEnglishItem = forcedAssessment[lastEnglishLocaleIndex];

    // Return true if this is the last item in the array OR if its sequence is 'BREAK'
    return (
      lastEnglishLocaleIndex === forcedAssessment.length - 1 ||
      lastEnglishItem.sequence === 'BREAK'
    );
  }

  // allows us to pass a custom close function to the modal. This is used to pass in
  // NULL if we want to close the modal via an internal button instead of the built in close feature
  openModal(content, props={}){
    let modal = (
      <Modal
        closeModal={props.close}
        delayedCloseButton={props.delayedCloseButton || false}
        delayedCloseButtonTimeoutOverride={props.delayedCloseButtonTimeoutOverride}
        closeButton={props.closeButton || false}
        backgroundClose={props.backgroundClose || false}
        key={props.key}
        hideBackground={props.hideBackground || false}>
        {content}
      </Modal>
    );

    this.setState({
      modal: modal,
    });
  }

  closeModal(onModalClose=null){
    this.setState({
      modal: null,
    }, onModalClose);
  }

  /**
   * Initiates mid-session mic troubleshoot flow.
   * Called when blank audio is detected during a reading session.
   * Resets preChecksDone and navigates to StudentPreChecks which will skip to ASR checks.
   * @param {Object} audioIssueType - Object containing audio issue flags (isClipped, deviceIssue)
   */
  startMidSessionTroubleshoot(audioIssueType = {}) {
    // Update the currentPathname FIRST to prevent the history.listen "back button" logic
    // from double-pushing and causing navigation cascades
    this.currentPathname = '/student/pre-checks';
    this.currentSearch = '';
    
    this.setState({
      midSessionMicTroubleshoot: true,
      audioIssueType: audioIssueType,
      preChecksDone: false,
      hideReaderContainer: false, // Show the background for the teacher modal
    }, () => {
      this.props.history.push('/student/pre-checks');
    });
  }

  /**
   * Restarts the session with the same URL params plus readyModuleTestMode=SKIP.
   * Called when mic test passes and user clicks "Restart Activity" in the troubleshoot flow.
   * When restarting from a mid-session troubleshoot, also sets midSessionTroubleshootSeen=true
   * so the next session knows the user has already been through the flow once.
   */
  restartSession(skipMicCheck = false) {
    const newParams = { ...this.initialUrlParams };
    if (skipMicCheck) {
      newParams.readyModuleTestMode = 'SKIP';
    }

    if (this.state.midSessionMicTroubleshoot) {
      newParams.midSessionTroubleshootSeen = 'true';
    }

    const searchParams = new URLSearchParams(newParams);

    // /student/pre-checks with readyModuleTestMode=SKIP skips the Ready to Start screen and goes straight into assess without user interaction
    // so we need to redirect to the home screen instead of the pre-checks screen
    const newUrl = `${window.location.origin}/student/home?${searchParams.toString()}`;
    window.location.href = newUrl;
  }

  /**
   * Restarts the session with suppressMidSessionTroubleshoot=true, preventing future
   * mid-session troubleshoot triggers for the remainder of the session.
   * Called when "Allow Student to Continue Session" is selected from the repeat troubleshoot interstitial.
   */
  restartSessionSuppressingTroubleshoot() {
    const newParams = { ...this.initialUrlParams };
    newParams.suppressMidSessionTroubleshoot = 'true';
    newParams.midSessionTroubleshootSeen = 'true';
    newParams.readyModuleTestMode = 'SKIP';

    const searchParams = new URLSearchParams(newParams);
    // /student/pre-checks with readyModuleTestMode=SKIP skips the Ready to Start screen and goes straight into assess without user interaction
    // so we need to redirect to the home screen instead of the pre-checks screen
    const newUrl = `${window.location.origin}/student/home?${searchParams.toString()}`;
    window.location.href = newUrl;
  }

  nextPage(callback=null){
    if(!this.state.paginatedStory || !this.state.paginatedStory.pages) return;
    if(this.state.pageIndex < this.state.paginatedStory.pages.length - 1){
      this.setState({
        pageIndex: this.state.pageIndex + 1,
      }, callback);
    }
  }

  previousPage(){
    if(this.state.pageIndex > 0){
      this.setState({
        pageIndex: this.state.pageIndex - 1,
      });
    }
  }

  setInterventions(interventions){
    this.setState({
      interventions: interventions,
    });
  }

  resetForcedStory(){
    this.setState({
      forceStory: null,
      studentAssignment: null,
    });
  }

  setNextStory(storyId){
    this.setState({
      forceStory: storyId,
    }, () => {
      this.resetStory();
    });
  }

  resetStory(){
    if (process.env.NODE_ENV !== 'production' && this.initialUrlParams.mockAOS !== 'true') {
      clearDevStickyMockAosState();
    }
    this.setState({
      activityIndex: this.state.activityIndex + 1, // increment the index each time an activity is finished
      paginatedStory: null,
      pageIndex: 0,
      readerKey: this.state.readerKey + 1,  // increment the reader key each time we reset the story to ensure that we wipe the component state clean as well as simply wiping the story away
    });
  }

  saveSessionTime() {
    const { activityId } = getUserData();
    const timeSpent = this.totalReadingTime.end - this.totalReadingTime.start;
    // GT-327: milliseconds the page was visible, written alongside the wall-clock sessionTime.
    studentRecordService.saveSessionTime(activityId, timeSpent, this.totalReadingTime.activeTime)
      .catch((error) => {
        console.error('Error saving session time:', error);
        logSessionInfo('error_saving_session_time', {
          error: error?.message || error,
          activityId
        });
      });
  }

  //Log the time when the story finishes
  logTimeOnStoryEnd(){
    this.totalReadingTime.end = new Date();
    this.totalReadingTime.activeTime = engagementClock.stop();
    this.saveSessionTime();
  }

  //Log the time when the story loads
  logTimeOnStoryStart(){
    this.totalReadingTime.start = new Date();
    this.totalReadingTime.activeTime = undefined; // set again by stop() at story end
    engagementClock.start();
  }

  setAssessmentStoryDirectly(rawStory, callback = null) {
    document.title = `Student Assessment - ${GetBrandedAppName()}`;

    this.setState({
      rawStory: rawStory,
      orfMode: rawStory.type === assignmentTypes.ASSESSMENT,
    });

    let paginatedStory = processAssessmentStory(rawStory);
    this.setState({
      paginatedStory,
      pageIndex: 0,
     }, callback);
  }

  //pulls a story based on ID, paginates that story, and sets it for the reader
  setStory(id, config, callback=null){
    document.title = config.assessmentMode ? `Student Assessment - ${GetBrandedAppName()}` : `Tutor Story - ${GetBrandedAppName()}`;

    if(config.assessmentMode){ // get the story in assessment format if this is assessment mode
      getAssessmentStory(id).then((paginatedStory) => {
        this.setState({paginatedStory}, callback);
      });
    } else if(config.squashedStory){  //squash the story if necessary
      getSquashedStory(id).then((paginatedStory) => {
        this.setState({paginatedStory}, callback);
      });
    } else {

      let phrasesPerPage = 3;
      if (window.innerHeight <= 768) {
        phrasesPerPage = 1;
      }
      if (this.state.forcedPhrasesPerPage) {
        phrasesPerPage = this.state.forcedPhrasesPerPage;
      }

      getPaginatedStory(id, phrasesPerPage).then((paginatedStory) => {
        this.setState({paginatedStory}, callback);
      });
    }
  }

  setMidAssessment(){
    //there doesnt need to be a way to turn off this flag because we log out at the end of an assessment. if this changes, change this
    this.setState({midAssessment: true});
  }

  async fetchAndSetCurrentPeriodInfo(session) {
    if (session.schoolId) {
      try {
        const periodInfo = await calendarService.getCurrentPeriod(session.schoolId);
        if (periodInfo) {
          const period = periodInfo.periodIdentifier;
          const schoolYear = periodInfo.schoolYear;

          // Wait for setState to complete before resolving
          return new Promise((resolve) => {
            this.setState({ period, schoolYear }, resolve);
          });
        }
      } catch (error) {
        console.error("Error fetching current period info:", error);
        // Continue without current period info
      }
    }
  }

  isActiveAsrPreload(token){
    return this.asrPreloadToken === token;
  }

  /**
   * Whether any download progress was credited this attempt — the retry gate's
   * "attempted" condition (TT-1301). A single accessor so the settle-path gate and the
   * stall-watchdog log field cannot drift: the `< 1` version of exactly this expression
   * living in two places is what let the highWaterProgress==1 case slip the retry gate
   * and under-count in telemetry. Note "credited" includes bytes already in the cache
   * (precacheAsrAssets credits approxBytes before the first fetch), so a fully-cached
   * bucket that then fails validation reads true here having fetched nothing.
   */
  get asrDownloadWasAttempted(){
    return this.asrHighWaterProgress > 0;
  }

  setAsrStateForActivePreload(token, nextState){
    if (this.isActiveAsrPreload(token)) {
      this.setState(nextState);
    }
  }

  /**
   * Records the cache layer's classification of why a preload failed, for the gate
   * below to consult. Token-guarded like every other preload callback so a stale
   * attempt settling after the watchdog (or a retry) bumped the token cannot
   * overwrite the live attempt's reason.
   *
   * @param {number} token Preload attempt this outcome belongs to.
   * @param {string|null} reason An ASR_PRELOAD_FAILURE_REASON value, or null.
   */
  setAsrOutcomeForActivePreload(token, reason){
    if (this.isActiveAsrPreload(token)) {
      this.asrPreloadFailureReason = reason || null;
    }
  }

  setAsrProgressForActivePreload(token, fraction){
    if (!this.isActiveAsrPreload(token)) {
      return;
    }

    this.asrPendingProgress = Math.max(0, Math.min(fraction || 0, 1));
    if (this.asrPendingProgress > this.asrHighWaterProgress) {
      this.asrHighWaterProgress = this.asrPendingProgress;
    }

    const now = Date.now();
    const elapsed = now - this.lastAsrProgressUpdate;
    if (elapsed >= ASR_PROGRESS_UPDATE_INTERVAL_MS) {
      const progress = this.asrPendingProgress;
      this.asrPendingProgress = null;
      this.lastAsrProgressUpdate = now;
      this.setAsrStateForActivePreload(token, { asrDownloadProgress: progress });
      return;
    }

    if (this.asrProgressTimeout) {
      return;
    }

    this.asrProgressTimeout = setTimeout(() => {
      this.asrProgressTimeout = null;
      if (!this.isActiveAsrPreload(token) || this.asrPendingProgress === null) {
        return;
      }

      const progress = this.asrPendingProgress;
      this.asrPendingProgress = null;
      this.lastAsrProgressUpdate = Date.now();
      this.setAsrStateForActivePreload(token, { asrDownloadProgress: progress });
    }, ASR_PROGRESS_UPDATE_INTERVAL_MS - elapsed);
  }

  /**
   * The single loading-bar value, 0..100: the real byte-weighted download fills its share,
   * then the ESTIMATED engine-boot slice fills the rest up to a ceiling; 100 only once the
   * gate is actually open. See asr/localAsrBoot for the split and the ceiling (TT-1313).
   * @returns {number}
   */
  getAsrLoadProgressPercent(){
    return Math.round(compositeAsrLoadProgress({
      downloadFraction: this.state.asrDownloadProgress || 0,
      bootFraction: this.state.asrBootProgress || 0,
      done: Boolean(this.state.asrAssetsReady),
    }) * 100);
  }

  /**
   * Records the first and latest download fractions of the active attempt so the settle
   * path can turn (bytes fetched / time fetching) into a link-speed sample for the cold
   * boot estimate. The first value is what precacheAsrAssets credited for already-cached
   * bytes before any fetch, so the difference is the bytes that actually crossed the wire.
   * @param {number} token
   * @param {number} fraction
   */
  noteAsrDownloadFraction(token, fraction){
    if (!this.isActiveAsrPreload(token)) {
      return;
    }
    const clamped = Math.max(0, Math.min(fraction || 0, 1));
    if (this.asrInitialProgressFraction === null) {
      this.asrInitialProgressFraction = clamped;
    }
    this.asrLatestProgressFraction = Math.max(this.asrLatestProgressFraction, clamped);
  }

  /**
   * Link speed measured on the precache download just completed, or null when too little
   * was fetched (or for too short a time) to mean anything — a warm Cache Storage fetches
   * only what a version bump moved, often nothing.
   *
   * APPROXIMATE, and only ever used to shape the estimated boot bar (never a timeout):
   * `approxBytes` are DECODED sizes while the runtime/wasm entries cross the wire
   * brotli-compressed (~0.9 MB for ~3.6 MB decoded), so this overstates wire bytes; and
   * the window runs to the download SETTLING, which includes validation and the cache
   * writes, so it overstates wire time. The two biases pull opposite ways and neither is
   * corrected. Do not reuse this as a link-speed measurement elsewhere.
   * @param {object} manifest The active manifest (approxBytes weights).
   * @returns {number|null} bytes per second
   */
  measuredAsrDownloadThroughput(manifest){
    if (!this.asrFirstFetchActivityAt || !this.asrDownloadSettledAt || this.asrInitialProgressFraction === null) {
      return null;
    }
    const totalBytes = (manifest?.expectedFiles || []).reduce((sum, file) => sum + (file?.approxBytes || 0), 0);
    const fetchedBytes = (this.asrLatestProgressFraction - this.asrInitialProgressFraction) * totalBytes;
    const elapsedMs = this.asrDownloadSettledAt - this.asrFirstFetchActivityAt;
    if (fetchedBytes < ASR_THROUGHPUT_MIN_SAMPLE_BYTES || elapsedMs < ASR_THROUGHPUT_MIN_SAMPLE_MS) {
      return null;
    }
    return fetchedBytes / (elapsedMs / 1000);
  }

  /**
   * Boots the shared local ASR engine behind the loading gate (TT-1313 / CR-10679) and
   * opens the gate on the outcome. Runs only for the still-active preload attempt, after
   * its download settled ready. Sequence:
   *
   *  1. probe whether the model is already extracted in IDBFS (warm) or the worker must
   *     download it (cold) — that picks the bar estimate and the hard timeout;
   *  2. drive the estimated boot slice of the bar on a ticker (linear over the estimate,
   *     parked at the ceiling if the boot runs long);
   *  3. race the boot against the timeout. Success opens the gate with local ASR on.
   *     Failure or timeout opens it with local ASR OFF (server fallback, no retry modal —
   *     a retry would re-run the same boot into the same device), records why, and samples
   *     storage at the failure so disk-full is separable from eviction.
   *
   * On timeout the engine is told to dispose: the in-flight createModel cannot be
   * interrupted (the worker is not reachable until it resolves), so SpeechStream's lifecycle
   * bump makes the late init tear its handler down on settle. Until then the worker keeps
   * downloading/extracting into IDBFS — which is why a cold device that times out is warm
   * next session rather than starting over.
   *
   * The stall watchdog is already stopped by the time this runs (the download settled), so
   * the boot's own timeout is the only bound here — deliberately: the watchdog keys on
   * download progress and fetch activity, neither of which the boot produces.
   * @param {number} token
   * @returns {Promise<void>}
   */
  async bootLocalAsrForActivePreload(token){
    // Armed BEFORE the first await: asrPreloadPromise is already null by now, so this flag
    // is the only thing that stops a re-entrant preloadLocalAsrAssets during the probe.
    this.asrBootInFlight = true;
    try {
      await this.runGatedLocalAsrBoot(token);
    } catch (error) {
      // Nothing in the boot is supposed to throw, but the failure mode if something does
      // is "the student cannot start": gate closed, no retry button, every re-entry a no-op.
      // So a throw is treated exactly like a failed boot. The gate opens FIRST; telemetry
      // is best-effort after it.
      //
      // Guard the timer cleanup exactly as runGatedLocalAsrBoot's finally does: only the
      // still-active attempt owns the ticker/timeout handles. Clearing them for a superseded
      // attempt would strip the replacement's hard timeout, so a wedged replacement boot
      // would hold the loading gate indefinitely instead of falling back (PR #6006 review #2).
      if (!this.isActiveAsrPreload(token)) {
        return;
      }
      this.stopAsrBoot();
      this.asrBootInFlight = false;
      this.setAsrStateForActivePreload(token, {
        asrAssetsReady: true,
        localAsrReady: false,
        asrDownloadError: false,
        asrDownloadProgress: 1,
        asrBootProgress: 0,
      });
      try {
        recordLocalAsrFailure('boot.threw', { attempt: token, error: error?.message });
        recordLocalAsrFailure('preload.fallbackToServer', {
          downloadWasAttempted: this.asrDownloadWasAttempted,
          highWaterProgress: this.asrHighWaterProgress,
          failureReason: 'bootThrew',
          retryExhausted: this.asrHasRetried,
        }, { expected: false });
      } catch (telemetryError) {
        console.error('local ASR boot: telemetry failed after a boot throw', telemetryError);
      }
    }
  }

  /**
   * The gated boot itself; only bootLocalAsrForActivePreload calls this, inside its guard.
   * @param {number} token
   * @returns {Promise<void>}
   */
  async runGatedLocalAsrBoot(token){
    const { presence, entries: idbfsEntries } = await probeLocalAsrModelPresence();
    if (!this.isActiveAsrPreload(token)) {
      // Superseded during the probe: abandon/unmount already cleared the flag (and disposed
      // if needed) — nothing to do for a boot nobody wants.
      return;
    }
    const manifest = getActiveAsrManifest();
    const plan = planLocalAsrBoot({
      presence,
      modelBytes: manifest?.modelArchiveBytes || 0,
      throughputBytesPerSec: this.measuredAsrDownloadThroughput(manifest),
    });
    const bootStartedAt = Date.now();
    // The student's TOTAL wait is what is budgeted: the boot gets what is left of the gate
    // budget after the download, never less than the warm bound (asr/localAsrBoot).
    const gateElapsedMs = this.asrPreloadStartedAt ? bootStartedAt - this.asrPreloadStartedAt : 0;
    // planLocalAsrBoot is pure arithmetic on its inputs, so do not mutate its result — keep the
    // gate-bounded timeout in a local and log/use that (PR #6006 review #8b).
    const bootTimeoutMs = boundBootTimeoutToGateBudget({ timeoutMs: plan.timeoutMs, gateElapsedMs });
    recordLocalAsrLifecycle('boot.started', { attempt: token, ...plan, timeoutMs: bootTimeoutMs, idbfsEntries, gateElapsedMs });
    // Flushed NOW, not at settle: the boot is where a 4 GB tab dies (CR-10679 is a white
    // screen), and the settle flush is up to the whole boot budget away. A session that
    // crashes mid-boot must still leave its preload.settled + boot.started rows behind —
    // that is the telemetry this change is judged by.
    flushLocalAsrLifecycle('bootStarted');
    // The download slice is full; the boot slice starts empty.
    this.setAsrStateForActivePreload(token, { asrDownloadProgress: 1, asrBootProgress: 0 });
    this.startAsrBootTicker(token, bootStartedAt, plan.estimateMs);

    let outcome;
    try {
      outcome = await Promise.race([
        ensureLocalPlacementAsrBooted().then(
          (booted) => (booted ? 'ready' : 'failed'),
          () => 'failed',
        ),
        new Promise((resolve) => {
          this.asrBootTimeout = setTimeout(() => resolve('timeout'), bootTimeoutMs);
        }),
      ]);
    } finally {
      // Only the still-active attempt owns the ticker/timeout handles; a superseded one
      // must not clear timers that now belong to its replacement.
      if (this.isActiveAsrPreload(token)) {
        this.stopAsrBoot();
      }
    }

    if (!this.isActiveAsrPreload(token)) {
      // Abandoned (assessment classification / unmount) while booting: that path already
      // opened the gate, cleared asrBootInFlight and disposed the engine. Nothing to record
      // for a boot nobody wants.
      return;
    }
    this.asrBootInFlight = false;
    const durationMs = Date.now() - bootStartedAt;

    if (outcome === 'ready') {
      recordLocalAsrLifecycle('boot.succeeded', { attempt: token, presence: plan.presence, durationMs });
      this.setAsrStateForActivePreload(token, {
        asrAssetsReady: true,
        localAsrReady: true,
        asrDownloadProgress: 1,
        asrBootProgress: 1,
        asrDownloadError: false,
      });
    } else {
      if (outcome === 'timeout') {
        recordLocalAsrFailure('boot.timedOut', {
          attempt: token,
          presence: plan.presence,
          timeoutMs: bootTimeoutMs,
          estimateMs: plan.estimateMs,
          durationMs,
        });
        disposeLocalPlacementAsr();
      } else {
        recordLocalAsrFailure('boot.failed', { attempt: token, presence: plan.presence, durationMs });
      }
      // Failure-site storage snapshot (TT-1313): after A2 the disk-write that can still
      // fail is the worker's IDBFS extraction, which surfaces here as a boot failure, not
      // in the cache layer. Not awaited — diagnostic only, never on the gate's path.
      getStorageDiagnostics(this.asrPersistPromise).then((storage) => {
        recordLocalAsrLifecycle('boot.storageDiagnostics', { presence: plan.presence, outcome, ...storage });
        flushLocalAsrLifecycle('bootStorageDiagnostics');
      });
      recordLocalAsrFailure('preload.fallbackToServer', {
        downloadWasAttempted: this.asrDownloadWasAttempted,
        highWaterProgress: this.asrHighWaterProgress,
        failureReason: outcome === 'timeout' ? 'bootTimeout' : 'bootFailed',
        retryExhausted: this.asrHasRetried,
      }, { expected: false });
      this.setAsrStateForActivePreload(token, {
        asrAssetsReady: true,
        localAsrReady: false,
        asrDownloadError: false,
        asrDownloadProgress: 1,
        asrBootProgress: 0,
      });
    }
    // No flush here: preloadLocalAsrAssets flushes 'preloadSettled' right after this
    // returns, and that flush covers the boot events too.
  }

  /**
   * Animates the estimated boot slice: linear over `estimateMs`, then parked at 1 (the
   * composite maps that to the ceiling, never 100%). Stops itself once parked — the value
   * cannot change again until the boot settles.
   * @param {number} token
   * @param {number} startedAt
   * @param {number} estimateMs
   */
  startAsrBootTicker(token, startedAt, estimateMs){
    this.stopAsrBootTicker();
    this.asrBootTicker = setInterval(() => {
      if (!this.isActiveAsrPreload(token)) {
        this.stopAsrBootTicker();
        return;
      }
      const fraction = estimatedBootFraction(Date.now() - startedAt, estimateMs);
      // Render only when the visible percent moves: on a 180 s cold estimate the tick fires
      // ~720 times for 38 points of bar, and each setState re-renders the App tree while
      // the WASM engine is loading on the 4 GB devices this gate protects.
      if (Math.round(fraction * 100) !== Math.round((this.state.asrBootProgress || 0) * 100)) {
        this.setState({ asrBootProgress: fraction });
      }
      if (fraction >= 1) {
        this.stopAsrBootTicker();
      }
    }, ASR_BOOT_PROGRESS_TICK_MS);
  }

  stopAsrBootTicker(){
    if (this.asrBootTicker) {
      clearInterval(this.asrBootTicker);
      this.asrBootTicker = null;
    }
  }

  /** Clears the boot's ticker and hard timeout (not the engine — see abandon/unmount). */
  stopAsrBoot(){
    this.stopAsrBootTicker();
    if (this.asrBootTimeout) {
      clearTimeout(this.asrBootTimeout);
      this.asrBootTimeout = null;
    }
  }

  /**
   * Pre-caches client-side ASR runtime assets during the initial loading state so the
   * model/runtime are ready (and offline-capable) before the student presses
   * "Let's do it" (TT-825). Pins Cache Storage and downloads/validates the model
   * bundle and ORT runtime, reporting progress to the loading screen.
   *
   * Startup fallback model: when asset caching cannot complete, local ASR stays
   * disabled for the session and the loading gate opens so server placement can
   * handle reading. This prevents preload failures from blocking students. A
   * genuinely broken download is the one exception — it holds the gate closed and
   * offers a retry, because retrying can actually fix it. Which of the two applies
   * comes from the cache layer's reported reason, never from progress alone
   * (TT-1198).
   *
   * @returns {Promise<void>}
   */
  async preloadLocalAsrAssets(){
    // Local ASR is tutor-only, so hold the download until the session is classified.
    // Every student path through checkAssignments resolves it, including on error, so
    // this defers the preload rather than cancelling it.
    if (!isLocalAsrSessionModeResolved()) {
      return;
    }

    // The enablement decision is recorded on the FIRST classified entry, whether or not
    // it lets the preload proceed, because the opt-out paths used to log nothing at all:
    // a session on server ASR was indistinguishable in the logs from one where local ASR
    // silently never applied. Latched because the stall watchdog, the retry button, and
    // resolveLocalAsrSessionMode all re-enter here.
    if (!this.asrEnablementLogged) {
      this.asrEnablementLogged = true;
      recordLocalAsrLifecycle('enablement.resolved', {
        ...explainLocalAsrEnablement(),
        cacheSupported: isAsrCacheSupported(),
      });
      // Recorded alongside the decision, for enabled and opted-out sessions alike, so a
      // slow-decode report can be tied to hardware. Not awaited: it resolves UA-CH hints
      // and must not delay the preload. It never rejects.
      recordLocalAsrDeviceProfile();
    }

    if (!isLocalAsrEnabled() || !isAsrCacheSupported()) {
      return;
    }

    if (this.asrPreloadPromise) {
      return this.asrPreloadPromise;
    }

    // The download settled and the engine boot is running (TT-1313): the gate is about to
    // open on its outcome. A re-entry here (session-mode re-resolution) must not start a
    // second attempt — its stale twin would later clear the active attempt's timers.
    if (this.asrBootInFlight) {
      return;
    }

    if (this.asrProgressTimeout) {
      clearTimeout(this.asrProgressTimeout);
      this.asrProgressTimeout = null;
    }

    const token = this.asrPreloadToken + 1;
    this.asrPreloadToken = token;
    this.lastAsrProgressUpdate = 0;
    this.asrPendingProgress = null;
    this.asrHighWaterProgress = 0;
    this.asrLastFetchActivityAt = 0;
    this.asrPreloadFailureReason = null;
    this.asrFirstFetchActivityAt = 0;
    this.asrInitialProgressFraction = null;
    this.asrLatestProgressFraction = 0;
    this.asrDownloadSettledAt = 0;
    this.stopAsrBoot();
    // Fresh controller per attempt (TT-1267): abortActiveAsrPreload() cancels this
    // attempt's fetches if a retry/abandon/stall supersedes it. Guarded so a runtime
    // without AbortController simply passes an undefined signal, unchanged behavior.
    this.asrPreloadAbortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

    // Ask the browser to pin this origin so the large ASR assets are not evicted.
    // Kicked off here (it is independent of the download) but NOT awaited: the
    // grant result lands in the storageDiagnostics log below. Chrome grants
    // heuristically and can silently decline, and WebKit effectively no-ops, so the
    // real grant rate per device class is only knowable from field telemetry.
    // Latched across re-entries (stall watchdog, retry): persist() can raise a
    // permission prompt on some browsers, and one answer per app instance is enough.
    if (!this.asrPersistPromise) {
      this.asrPersistPromise = requestPersistentStorage();
    }

    this.setAsrStateForActivePreload(token, { asrDownloadError: false, asrBootProgress: 0 });

    this.startAsrStallWatchdog();

    const manifest = getActiveAsrManifest();
    const preloadStartedAt = Date.now();
    this.asrPreloadStartedAt = preloadStartedAt;
    recordLocalAsrLifecycle('preload.started', { attempt: token, version: manifest?.version });

    this.asrPreloadPromise = ensureAsrAssetsReady(manifest, {
      onProgress: (fraction) => {
        this.noteAsrDownloadFraction(token, fraction);
        this.setAsrProgressForActivePreload(token, fraction);
      },
      onOutcome: ({ reason }) => this.setAsrOutcomeForActivePreload(token, reason),
      onFetchActivity: () => {
        if (this.isActiveAsrPreload(token)) {
          const now = Date.now();
          this.asrLastFetchActivityAt = now;
          if (!this.asrFirstFetchActivityAt) {
            this.asrFirstFetchActivityAt = now;
          }
        }
      },
      signal: this.asrPreloadAbortController?.signal,
    });

    let ready;
    try {
      ready = await this.asrPreloadPromise;
    } catch (err) {
      // The watchdog (or a retry) may have already abandoned this preload by
      // bumping the token. Swallow the rejection so it never surfaces as an
      // unhandled promise rejection.
      if (!this.isActiveAsrPreload(token)) {
        return;
      }
      recordLocalAsrFailure('preload.rejected', { attempt: token, error: err?.message });
      ready = false;
    }

    // Recorded synchronously so the event keeps its ordering relative to the
    // fallback/ready events below and can never be deferred by the quota subsystem
    // or a pending persist() permission prompt.
    // `superseded` lets App-layer dashboards tell a deliberate supersession (retry/abandon/
    // stall/unmount bumped the token) apart from a genuine ready:false download failure.
    // Named for what the token check actually measures — supersession, not a confirmed
    // fetch cancellation — so it is NOT falsely equated with the cache layer's literal
    // `precacheAborted` when the two are read side by side (a supersession in a runtime
    // without AbortController bumps the token but cancels nothing). It is read BEFORE the
    // isActiveAsrPreload early return below, so a superseded attempt that RESOLVES
    // (ensureAsrAssetsReady resolves false on abort by contract, never rejects) still
    // emits exactly one settled event carrying this flag. A superseded attempt that
    // instead rejected would emit none — the catch above already returned on the stale
    // token — but that path is not reachable while the resolve-false contract holds.
    this.asrDownloadSettledAt = Date.now();
    recordLocalAsrLifecycle('preload.settled', {
      ready,
      superseded: !this.isActiveAsrPreload(token),
      attempt: token,
      durationMs: Date.now() - preloadStartedAt,
    });

    // Storage telemetry is sampled after the download so usage reflects the real
    // post-preload footprint (estimate() covers the whole origin bucket, so Cache
    // Storage AND the vosk worker's IDBFS model). Emitted as its own event,
    // deliberately NOT awaited: this is diagnostic only, and neither the loading
    // gate nor the settled log above may wait on it. getStorageDiagnostics never
    // rejects and bounds every step, so no catch is needed.
    getStorageDiagnostics(this.asrPersistPromise).then((storage) => {
      recordLocalAsrLifecycle('preload.storageDiagnostics', { ready, ...storage });
      // Flushed here rather than left to a later checkpoint: the runtime-init and
      // activity-start flushes only happen if the student actually enters an activity, so
      // a session that preloads and then idles would never persist this telemetry at all
      // — which is precisely the quota/persistence case it was added to investigate.
      flushLocalAsrLifecycle('storageDiagnostics');
    });

    if (!this.isActiveAsrPreload(token)) {
      return;
    }

    this.stopAsrStallWatchdog();

    this.asrPreloadPromise = null;
    // Abort-then-drop rather than a bare null (TT-1267): on the happy path the fetches
    // have already resolved and abort() is a no-op, but precacheAsrAssets also resolves
    // false when it loses its Promise.race to the overall timeout WITHOUT aborting the
    // signal — leaving its downloads still streaming. Nulling the controller there would
    // orphan those fetches beyond cancellation, so a retry would start a second
    // overlapping ~22.8MB download — exactly the double-download this ticket prevents.
    //
    // Deliberate tradeoff on the overall-timeout path: aborting discards the partial
    // (up to ~22.8MB, cache: 'no-store' with no range/resume) instead of letting it
    // finish in the background and warm the cache for the next session. That is the
    // right call here — a background download on a link already too slow to finish in
    // 5 minutes would contend with server-ASR audio streaming for the rest of the
    // session, and the student has fallen back to server ASR anyway. The cost is that
    // the slowest links re-download from zero next session rather than resuming.
    this.abortActiveAsrPreload();
    this.asrPendingProgress = null;

    if (ready) {
      // TT-1313 / CR-10679: the gate stays closed through the engine boot. Until now
      // localAsrReady flipped here and Speech booted the WASM engine in the background
      // at the very moment "Let's do it" enabled — so the download+extract+model-load
      // memory spike landed on top of the click into the intro video, white-screening
      // 4 GB Chromebooks. The boot now runs HERE, in the quiet window before the button,
      // behind the same loading bar (estimated slice), with its own bounded timeout.
      await this.bootLocalAsrForActivePreload(token);
    } else {
      // The progress number alone cannot tell "the download died" from "the asset
      // is not published yet": precacheAsrAssets credits already-cached bytes
      // before the first fetch, so a routine graph-revision bump against a
      // half-synced CDN opens at ~0.94 and would take the error branch — hard-
      // blocking the student behind a retry that cannot succeed until the deploy
      // finishes. The cache layer's reason is authoritative over the number
      // (TT-1198); it is only ever notPublished when EVERY failure was a missing
      // asset, so a genuine network failure still reaches the retry UI.
      //
      // TT-1301: the gate is `> 0`, NOT `> 0 && < 1`. A genuine failure can leave
      // highWaterProgress at exactly 1.0 (the progress clone can drain every byte
      // before cache.put rejects, and the min(bytes/approxBytes, 1) clamp saturates
      // when the real payload exceeds the estimate), and a validation failure reaches
      // here at 1.0 too. The old `< 1` clause denied all of those a retry, conflating
      // a broken download at 1.0 with a legitimate "nothing downloaded" (== 0).
      // highWater == 0 still gets no retry; notPublished is still gated below.
      const downloadWasAttempted = this.asrDownloadWasAttempted;
      const assetsNotPublished = this.asrPreloadFailureReason === ASR_PRELOAD_FAILURE_REASON.NOT_PUBLISHED;
      // TT-1266: show the retry UI at most once per session. On the first genuine
      // failure (download attempted, not notPublished, retry not yet used), show
      // the retry modal. On any subsequent failure after the user has retried, or
      // when the assets are simply not published / no download was attempted,
      // degrade to server ASR.
      if (downloadWasAttempted && !assetsNotPublished && !this.asrHasRetried) {
        recordLocalAsrFailure('preload.retryOffered', {
          highWaterProgress: this.asrHighWaterProgress,
          failureReason: this.asrPreloadFailureReason,
        }, { expected: false });
        this.setAsrStateForActivePreload(token, {
          asrDownloadError: true,
          asrDownloadProgress: this.asrHighWaterProgress,
        });
      } else {
        // `expected` when the assets simply are not published: still a real
        // degradation (the session loses local ASR), but a known normal state
        // during a deploy rather than something to go looking at, so it records
        // at info instead of warn.
        recordLocalAsrFailure('preload.fallbackToServer', {
          downloadWasAttempted,
          highWaterProgress: this.asrHighWaterProgress,
          failureReason: this.asrPreloadFailureReason,
          retryExhausted: this.asrHasRetried,
        }, { expected: assetsNotPublished });
        this.setAsrStateForActivePreload(token, {
          asrAssetsReady: true,
          localAsrReady: false,
          asrDownloadError: false,
          asrDownloadProgress: 1,
        });
      }
    }

    // Pre-activity checkpoint. No activity exists yet, so this object is keyed by
    // hex-of-userId rather than an activity — the activity-start flush re-emits the
    // same accumulated timeline under the activity key. The still-pending storage
    // diagnostics get their own flush when they resolve.
    flushLocalAsrLifecycle('preloadSettled');
  }

  /**
   * Whether this session is allowed to launch an assessment at all, independent of
   * which assignment comes back. Combines the assessment entitlements with the
   * forced-tutor overrides, and is shared with checkAssignments so the local ASR
   * classification and the actual launch decision cannot drift apart.
   *
   * @param {object} session Current user session (getUserData()).
   * @returns {boolean}
   */
  canSessionLaunchAssessment(session){
    const forcedMode = this.isForcedLaunchMode(session);
    const assessmentEntitled = session.assessmentEnabled || session.screenerEnabled ||
      this.props.spanishIsipCalibrationGuid || this.props.isSpanishPilot;
    return Boolean(assessmentEntitled) && !forcedMode;
  }

  /**
   * Demo and QA launches that bypass entitlements. Shared by canSessionLaunchAssessment and
   * canSessionAccessAmira so both agree on what a forced launch is.
   * @param {object} session Current user session (getUserData()).
   * @returns {boolean}
   */
  isForcedLaunchMode(session){
    return Boolean(session.demoMode || this.props.forceTutor || this.state.forceStory || this.props.forceIntervention);
  }

  /**
   * Whether the student's school has any Amira product turned on at all (ASSMNT-2567 /
   * CR-10770: a campus with Product Access off entitles nothing, but the student could still
   * reach the home screen and dead-end there). Same rule as amira-educators-client's
   * NO_SUBSCRIPTIONS dashboard block: the school's license subscriptions, any product true.
   *
   * Reads subscriptions, not tutorEnabled/assessmentEnabled/screenerEnabled: those three miss
   * Tutora, Enseñar and Instruct, and the app-settings kill switches and per-student overrides
   * rewrite them. Missing subscriptions (license lookup failed) is not a verdict, so it fails open.
   * An object with no product true blocks even if its keys are null or absent, on purpose: the
   * educators dashboard treats that shape as "no subscriptions" too.
   *
   * Exempt: forced launches, the Spanish pilot flags, and teacherMicTest (a teacher diagnosing a
   * device). goToProgressPage is not exempt even though handleStudentInboxTutor exempts it --
   * with Product Access off the progress page is the CR-10770 dead end.
   *
   * @param {object} session Current user session (getUserData()).
   * @returns {boolean}
   */
  canSessionAccessAmira(session){
    const isExemptLaunch = this.isForcedLaunchMode(session)
      || this.props.spanishIsipCalibrationGuid || this.props.isSpanishPilot || this.props.teacherMicTest;
    if (isExemptLaunch) {
      return true;
    }
    const { subscriptions } = session;
    if (!subscriptions) {
      return true;
    }
    return SCHOOL_PRODUCT_KEYS.some(product => subscriptions[product] === true);
  }

  /**
   * Classifies the session as assessment or tutor for local ASR and releases the asset
   * preload that waits on that answer (TT-828).
   *
   * Local ASR is tutor-only, and the preload would otherwise start before the
   * assignment is known, so assessments downloaded a model they never used — and then
   * initialized it, which surfaced late on-device transcripts during the pre-check ASR
   * story. Assessment sessions open the loading gate immediately instead, since there
   * is nothing left to wait for.
   *
   * @param {boolean} isAssessment
   * @returns {void}
   */
  resolveLocalAsrSessionMode(isAssessment){
    setLocalAsrAssessmentSession(isAssessment);
    if (isLocalAsrAssessmentSession()) {
      // Recorded here rather than left to preloadLocalAsrAssets' enablement event: this
      // path returns before it, and an assessment session is the single most common
      // reason a session runs on server ASR.
      recordLocalAsrLifecycle('enablement.assessmentSession', { preloadAbandoned: Boolean(this.asrPreloadPromise) });
      this.abandonLocalAsrPreload();
      return;
    }
    this.preloadLocalAsrAssets();
  }

  /**
   * Cancels the active preload's in-flight fetches (TT-1267). Aborts the current
   * AbortController and drops the controller reference only — it deliberately does NOT
   * touch this.asrPreloadPromise, because the stall watchdog leaves that promise set on
   * purpose so retryLocalAsrDownload knows an abandoned attempt exists (see the watchdog
   * fire below). Every abandon path (retry, assessment-abandon, stall watchdog, unmount)
   * calls this so the ~22.8MB acoustic model is never downloaded by two overlapping
   * attempts at once.
   *
   * Precondition for SUPERSEDING callers (retry, assessment-abandon, stall, unmount):
   * bump this.asrPreloadToken FIRST. Aborting makes the in-flight ensureAsrAssetsReady
   * RESOLVE FALSE (it never rejects on abort — precacheAsrAssets catches everything and
   * returns false), and the token bump is what makes that resolve-false read as stale
   * (isActiveAsrPreload is false, so the settle path returns early) instead of surfacing a
   * deliberate abort to the student as preload.fallbackToServer with asrDownloadError. The
   * settle path itself is the one caller that does NOT bump first: it runs for the
   * still-active attempt after it has already resolved, only to cancel fetches the
   * overall-timeout leg may have left streaming.
   */
  abortActiveAsrPreload(){
    if (this.asrPreloadAbortController) {
      this.asrPreloadAbortController.abort();
      this.asrPreloadAbortController = null;
    }
  }

  /**
   * Records a deliberately torn-down boot as its own outcome and flushes it. Called from
   * both teardown sites — abandonLocalAsrPreload (an assessment classification arriving
   * after boot.started) and componentWillUnmount (auth drop / logout mid-boot). Neither
   * records a boot terminal, so without this the analyzer would sweep the row into its
   * 'unfinished' (white-screen) bucket — inflating the exact metric the rollout rests on
   * (PR #6006 bugbot). Flushed like boot.storageDiagnostics because an unmount is the last
   * chance to persist it.
   *
   * Best-effort: both callers go on to open the gate and dispose the engine, and a throw
   * from telemetry must never strand the student behind a closed loading bar with the
   * WASM engine resident — the same rule bootLocalAsrForActivePreload's catch follows.
   * @returns {void}
   */
  recordLocalAsrBootAbandoned(){
    try {
      recordLocalAsrLifecycle('boot.abandoned', {
        wasBooting: this.asrBootInFlight,
        wasReady: this.state.localAsrReady,
      });
      flushLocalAsrLifecycle('bootAbandoned');
    } catch (telemetryError) {
      console.error('local ASR boot: abandon telemetry failed', telemetryError);
    }
  }

  /**
   * Drops any in-flight ASR asset preload and opens the loading gate with local ASR
   * off. Reached when a session turns out to be an assessment, including after a
   * preload already started (for example an assessment surfaced by refreshAssignments).
   *
   * The token bump is the same abandon mechanism retryLocalAsrDownload uses: in-flight
   * fetches are cancelled via abortActiveAsrPreload, and any that still settle no-op
   * because their token is stale.
   */
  abandonLocalAsrPreload(){
    this.asrPreloadToken += 1;
    this.abortActiveAsrPreload();
    this.asrPreloadPromise = null;
    this.asrPendingProgress = null;
    this.stopAsrStallWatchdog();
    // An engine booted (or booting) for a session that turned out to be an assessment
    // has no consumer — assessments never decode locally — so tear it down rather than
    // leave its WASM heap resident for the whole assessment on the 4 GB devices this gate
    // protects. Mid-boot, the dispose bumps SpeechStream's lifecycle so the in-flight init
    // disposes its handler when createModel settles; after a finished boot it disposes
    // the live engine directly.
    if (this.asrBootInFlight || this.state.localAsrReady) {
      this.recordLocalAsrBootAbandoned();
      this.asrBootInFlight = false;
      disposeLocalPlacementAsr();
    }
    this.stopAsrBoot();
    if (this.asrProgressTimeout) {
      clearTimeout(this.asrProgressTimeout);
      this.asrProgressTimeout = null;
    }
    this.setState({
      asrAssetsReady: true,
      localAsrReady: false,
      asrDownloadError: false,
      asrDownloadProgress: 0,
      asrBootProgress: 0,
    });
  }

  /**
   * Retries the on-device ASR asset download after a failure, re-opening the progress
   * flow from the loading screen (TT-825).
   */
  retryLocalAsrDownload(){
    if (!this.state.asrDownloadError) {
      return;
    }
    if (this.asrPreloadPromise) {
      // Abandon the stale preload by invalidating its token so its settle path
      // no-ops, and abort its in-flight fetches so it stops downloading rather than
      // racing this retry for the same ~22.8MB acoustic model (TT-1267). Any fetch
      // that still settles is guarded from state updates by the token check.
      this.asrPreloadToken += 1;
      this.abortActiveAsrPreload();
      this.asrPreloadPromise = null;
    }

    this.asrHasRetried = true;
    this.setState({ asrDownloadError: false, asrDownloadProgress: 0, asrBootProgress: 0 });
    this.preloadLocalAsrAssets();
  }

  /**
   * Re-attempts ASR asset download when the browser regains network connectivity.
   * Only retries if the previous attempt ended with an error; avoids redundant
   * retries when the download is still in progress or already succeeded.
   */
  handleOnline() {
    if (this.state.asrDownloadError) {
      // This counts toward the one-retry budget (TT-1266). The budget bounds
      // the total number of full download re-attempts regardless of trigger —
      // a network-recovery retry is the same ~28 MB cost as a manual one, and
      // on the slow links this targets, a second full attempt is the expense
      // we are capping.
      recordLocalAsrLifecycle('preload.networkRecoveryRetry');
      this.retryLocalAsrDownload();
    }
  }

  /**
   * Starts a watchdog that fires if asrDownloadProgress does not advance for
   * ASR_STALL_CHECK_INTERVAL_MS while the download is still pending AND the cache
   * layer has been idle longer than FETCH_TIMEOUT_MS. The cache-layer gate prevents
   * false-positives on cold-cache slow-first-byte, where progress sits at 0 while a
   * fetch is legitimately establishing (TT-1268). Shows the retry UI so the student
   * is not stranded on a frozen progress bar.
   */
  startAsrStallWatchdog() {
    this.stopAsrStallWatchdog();
    this.asrLastWatchdogProgress = Math.max(this.state.asrDownloadProgress || 0, this.asrPendingProgress || 0);

    this.asrStallWatchdogTimeout = setTimeout(() => {
      this.asrStallWatchdogTimeout = null;
      if (this.state.asrAssetsReady || this.state.asrDownloadError) {
        return;
      }
      const current = Math.max(this.state.asrDownloadProgress || 0, this.asrPendingProgress || 0);
      // TT-1268: only declare a stall when progress hasn't advanced AND the cache
      // layer has been idle longer than its own header timeout. The falsy check on
      // asrLastFetchActivityAt (reset to 0 each attempt) means "no fetch has started
      // yet" — treat that as idle so the watchdog still fires when the cache layer
      // never reaches a fetch (e.g. SW registration hangs).
      const now = Date.now();
      const cacheLayerIdle = !this.asrLastFetchActivityAt
        || (now - this.asrLastFetchActivityAt) >= FETCH_TIMEOUT_MS;
      if (current <= this.asrLastWatchdogProgress && current < 1 && cacheLayerIdle) {
        const cacheIdleMs = this.asrLastFetchActivityAt
          ? now - this.asrLastFetchActivityAt
          : null;
        recordLocalAsrFailure('preload.progressStalled', { progress: current, cacheIdleMs });
        // Invalidate the token so the stale preload's settle path no-ops, and abort
        // its in-flight fetches so a genuinely stalled download stops consuming the
        // network instead of running on in the background (TT-1267).
        this.asrPreloadToken += 1;
        this.abortActiveAsrPreload();
        // TT-1266: if the user already used their one retry, degrade to server
        // ASR instead of re-showing the retry modal.
        if (this.asrHasRetried) {
          recordLocalAsrFailure('preload.fallbackToServer', {
            // TT-1301: shared accessor so this log field cannot drift from the
            // settle-path gate. The watchdog's own stall predicate above intentionally
            // keeps its separate `< 1` guard.
            downloadWasAttempted: this.asrDownloadWasAttempted,
            highWaterProgress: this.asrHighWaterProgress,
            failureReason: this.asrPreloadFailureReason,
            retryExhausted: true,
          }, { expected: false });
          this.asrPreloadPromise = null;
          if (this.asrProgressTimeout) {
            clearTimeout(this.asrProgressTimeout);
            this.asrProgressTimeout = null;
          }
          this.setState({
            asrAssetsReady: true,
            localAsrReady: false,
            asrDownloadError: false,
            asrDownloadProgress: 1,
          });
        } else {
          recordLocalAsrFailure('preload.retryOffered', {
            highWaterProgress: this.asrHighWaterProgress,
            failureReason: this.asrPreloadFailureReason,
          }, { expected: false });
          // asrPreloadPromise is intentionally left set so retryLocalAsrDownload
          // knows an abandoned attempt exists and can clear it on retry —
          // abortActiveAsrPreload only drops the controller, never the promise.
          this.setState({ asrDownloadError: true });
        }
      } else {
        this.asrLastWatchdogProgress = current;
        this.startAsrStallWatchdog();
      }
    }, ASR_STALL_CHECK_INTERVAL_MS);
  }

  stopAsrStallWatchdog() {
    if (this.asrStallWatchdogTimeout) {
      clearTimeout(this.asrStallWatchdogTimeout);
      this.asrStallWatchdogTimeout = null;
    }
  }

  /**
   * Fetches and processes student assignments.
   * 
   * @param {Object} session - The user data containing student information.
   * @param {boolean} isRefresh - Flag indicating if assignments are being refreshed after a tutor completion. Defaults to false.
   */
  // Sets up state to launch an assessment from a manifest. Extracted so the BTS
  // bilingual chooser can launch a single chosen-locale manifest, while the normal
  // path keeps rolling up the full assignment.manifest for activity parenting.
  launchAssessmentManifest(assignment, launchManifest, isNonVerbalAssignment, rollupManifest = assignment.manifest) {
    // Every assessment launch funnels through here (normal, BTS single-language,
    // Spanish calibration), so this is the definitive point to disable local ASR.
    this.resolveLocalAsrSessionMode(true);

    //rollup all manifest activities to be compatible with the activity parenting logic
    rollupManifest.forEach(item => {
      const {activity} = item;
      if (activity && activity.length) {
        activityStack = activityStack.concat(activity.map(a => a.activityId));
        activityObjectStack = activityObjectStack.concat(activity);
      }
    });

    const nextManifestItem = launchManifest[0];

    // need to find the assessment id as the parent activity id
    let parentActivityId = null;
    if(activityObjectStack.length){
      parentActivityId = activityObjectStack.find(a => a.type === "assessment")?.activityId || activityObjectStack[activityObjectStack.length - 1].activityId;
    }

    const useAOS = shouldUseAOSForAssignment(assignment.assignmentId, assignment.assignmentType, launchManifest, this.props.amiraIsipPilotGuid || this.props.spanishIsipCalibrationGuid || this.props.isSpanishPilot, this.props.isSpanishPilot);

    this.setState({
      loading: false,
      studentAssignment: assignment,
      forcedAssessment: launchManifest,
      parentActivityId: parentActivityId,
      orfMode: !useAOS && nextManifestItem.type === assignmentTypes.ASSESSMENT, // For AOS, next call to getTestlet will determine orfMode
      shouldUseAOS: useAOS || isMockAOSNow(),
      isNonVerbalAssignment: isNonVerbalAssignment,
    });
  }

  async checkAssignments(session, isRefresh = false){
		var promise = null;
    // Fetch current period info with timeout protection
    try {
      await withTimeout(
        this.fetchAndSetCurrentPeriodInfo(session),
        15000,
        'fetchAndSetCurrentPeriodInfo timeout after 15s'
      );
    } catch (error) {
      console.error("Error fetching current period info:", error);
      logSessionInfo("error_fetch_period_info_check_assignments", {error: error.message});
      // Continue without period info
    }

		if (this.props.spanishIsipCalibrationGuid) {
      // Spanish calibration flow - fetch or create assignment
      promise = getSpanishCalibrationAssignment(session.userId, this.props.spanishIsipCalibrationGuid, this.state.schoolYear || 2025, this.state.period || 4);
		} else if (this.props.fromStudentInbox) {
      if (this.props.assignmentId) {
        promise = getAssignmentByIdForStudent(this.props.assignmentId);
      } else {
  			promise = getAssignmentForStudent(session.userId, session, this.props.assignmentId, this.props.returnUrl, this.props.amiraIsipPilotGuid);
      }
		} else {
			promise = amiraAssignmentWrapper.getNextAssignment({isNonInboxUser: true});
		}
    // Use the new Amira assignment service to get the next assignment
    try {
      // Add timeout for assignment fetch (20 second timeout)
      const assignment = await withTimeout(
        promise,
        20000,
        'Assignment fetch timeout after 20s'
      );

      // Filter out microLesson and istationLegacyActivity
      stripInvalidManifestActivities(assignment);

      // check for a calibration type and transform if so. TODO: have the backend handle this in the future
      if(assignment && (assignment.assignmentType === "CALIBRATION" || this.props.forceCalibration) && !this.props.spanishIsipCalibrationGuid){
        console.log("Overriding calibration assignment")
        const {actualGrade, sisGrade} = getUserData();
        const gradetoUse = sisGrade ? sisGrade : actualGrade;
        assignment = await getCalibrationAssignment(gradetoUse,assignment,this.props.forceCalibration);
        setUserData("screenerOverrides", getScreenerOverrides(assignment));
        this.setLocale('en-us');
        this.setBilingualModeLocale('en-us');
        this.setBilingualMode();
      }

      console.log("ASSIGN",assignment);

      // Check if student has nonverbal setting from metadata
      const isNonVerbalAssignment = session.nonverbal || false;

      // Check if assignment is an assessment type
      const isAssessment = assignment && assignment.assignmentType && isAssessmentTypeAssignment(assignment.assignmentType);

      if (isAssessment && this.canSessionLaunchAssessment(session)) {
        // Handle assessment assignment - launch the assessment

        // Fetch the most recent theta score for this student from completed assessments if it doesn't already exist
        if (!getUserData().THETA) {
          try {
            const theta = await studentRecordService.getLastUserTheta(session.userId);
            if (theta !== null && theta !== undefined) {
              setUserData('THETA', theta);
            }
          } catch (error) {
            console.error('Error fetching last user theta:', error);
          }
        }

        const assignmentConfig = getAssignmentConfiguration(assignment);
        setUserData('assignmentConfiguration', assignmentConfig);
        let remainingManifest = assignment.manifest;
        this.shouldSkipNextReadingComp = false; // Reset at the start of each assessment
        if (!this.props.amiraIsipPilotGuid && !this.props.spanishIsipCalibrationGuid && !this.props.isSpanishPilot) {
          // Add timeout for getPendingManifest (10 second timeout)
          remainingManifest = await withTimeout(
            getPendingManifest(assignment.manifest),
            10000,
            'getPendingManifest timeout after 10s'
          );
        }
        logSessionInfo("assignment_manifest",{remainingManifest});
        if (remainingManifest.length) {
          // BTS bilingual chooser — only when isBTSEffective() AND the student arrived via the
          // non-inbox path. Inbox/OLP (fromStudentInbox) and Spanish-calibration entries have
          // already selected a specific assignment upstream, so the chooser must not override
          // that choice. Non-BTS path is completely unchanged: falls straight through to
          // launchAssessmentManifest below.
          if (isBTSEffective() && !this.props.fromStudentInbox && !this.props.spanishIsipCalibrationGuid) {
            // A failure in the new bilingual detection (extra inbox load + subject
            // resolution) must not regress the already-resolved single-assessment launch,
            // so the whole block falls through to the default launch below on error.
            try {
              // Determine the valid English (READING) and Spanish (LECTURA) assessments via the
              // shared-services subject-aware, completion-filtered API — the same resolution
              // olp-student-experience uses. No manifest-locale scraping. Rule: both valid → show
              // the chooser; exactly one → launch it directly; neither → existing behavior.
              const { english, spanish } = await withTimeout(
                amiraAssignmentWrapper.resolveBilingualAssessments(),
                20000,
                'resolveBilingualAssessments timeout after 20s'
              );
              const enAssignment = english && isAssessmentTypeAssignment(english.assignmentType) ? english : null;
              const esAssignment = spanish && isAssessmentTypeAssignment(spanish.assignmentType) ? spanish : null;
              // Match the primary assignment: strip microLesson / istationLegacyActivity before
              // prefetching manifests or launching, so the BTS path can't surface activity types
              // the default assessment launch removes.
              stripInvalidManifestActivities(enAssignment);
              stripInvalidManifestActivities(esAssignment);

              // Prefetch each subject's pending manifest (timeout-wrapped like the normal path
              // above). checkAssignments is already async at load, so prefetching here keeps the
              // eventual pick handler fully synchronous — an await inside it would de-batch its
              // setStates in legacy React and trip the redirectToHome guard. A language is only
              // offerable if its manifest actually has something to launch: the package completion
              // filter and local getPendingManifest can disagree on "done", and launching an empty
              // manifest throws (launchManifest[0] is undefined).
              const [enManifest, esManifest] = await Promise.all([
                enAssignment
                  ? withTimeout(getPendingManifest(enAssignment.manifest), 10000, 'BTS getPendingManifest (en) timeout after 10s')
                  : Promise.resolve([]),
                esAssignment
                  ? withTimeout(getPendingManifest(esAssignment.manifest), 10000, 'BTS getPendingManifest (es) timeout after 10s')
                  : Promise.resolve([]),
              ]);
              const enLaunchable = enAssignment && enManifest.length ? enAssignment : null;
              const esLaunchable = esAssignment && esManifest.length ? esAssignment : null;

              if (enLaunchable && esLaunchable) {
                // Both languages launchable → defer the chooser until after the student clicks
                // "Let's do it" (onDonePlayingBootstrap); opening it here would stack it on top
                // of the Ready-to-Start modal. assignmentConfiguration is recomputed for the
                // actually-chosen locale in openBtsLanguageChooser, since the pair can differ in
                // locale from the config set above for `assignment`.
                // Both options are assessments, so classify now rather than at the pick:
                // the chooser opens from "Let's do it", which the ASR asset gate blocks
                // until the session is classified.
                this.resolveLocalAsrSessionMode(true);
                this.setState({
                  loading: false,
                  btsBilingualChoice: { enAssignment: enLaunchable, esAssignment: esLaunchable, enManifest, esManifest, isNonVerbalAssignment },
                });
                return;
              }

              // Exactly one launchable language → launch it directly, no chooser.
              const soleAssignment = enLaunchable || esLaunchable;
              if (soleAssignment) {
                const soleManifest = enLaunchable ? enManifest : esManifest;
                const soleLocale = enLaunchable ? 'en-us' : 'es-mx';
                // The subject-resolved assignment can be a different locale than `assignment`
                // (from getNextAssignment), so recompute the config AND set the locale from what
                // we actually launch — mirroring the chooser pick handler. Without setLocale the
                // session locale / copy strings / lang attribute would stay on the wrong language.
                setUserData('assignmentConfiguration', getAssignmentConfiguration(soleAssignment));
                // GT-413: same exception as the chooser's English card — a tutorInSpanish student
                // launching English keeps es-mx through the Ready Module; selectGrade sets en-us.
                if (esLaunchable || !getUserData().tutorInSpanish) {
                  this.setLocale(soleLocale);
                }
                this.launchAssessmentManifest(soleAssignment, soleManifest, isNonVerbalAssignment);
                return;
              }
              // Neither launchable → fall through to existing behavior below.
            } catch (error) {
              console.error('[BTS] bilingual assessment resolution failed; using default launch', error);
            }
          }

          this.launchAssessmentManifest(assignment, remainingManifest, isNonVerbalAssignment);
        } else {
          // If returnUrl is provided, navigate to it and return early
          if (this.props.fromStudentInbox) {
            window.location.href = this.props.returnUrl;
            return;
          }

          this.setState({
            loading: false
          })
          if (session.userType === userTypes.STUDENT && !isRefresh) {
            this.props.history.push("/student/home");
          }
        }
      } else {
        // Handle non-assessment assignment or no assignment - user goes to main menu
        // Answered from the rows saveUserData already fetched when they held a completed
        // activity; the network read behind this only runs when they did not.
        const anyCompleted = await withTimeout(
          hasAnyCompletedActivity(session.userId),
          10000,
          'hasAnyCompletedActivity timeout after 10s'
        );
        const forceTutor = session.tutorEnabled && !anyCompleted && !session.isEarlyReader;
        setUserData('forceTutor', forceTutor);

        if(anyCompleted){
          StudentInformationService.setFTUESeen(session.userId, true)
          setUserData('hasSeenFTUE', true);
        }

        let forceStory = null;
        let forceSkill = null;
        let studentAssignment = null;
        let manifestItemId = null;

        // Handle tutor, ERSS, EPS, textSet activity types as tutor stories
        if (assignment && assignment.manifest && assignment.manifest.length > 0) {
          // Add timeout for getPendingTutorManifest (10 second timeout)
          const tutorManifest = await withTimeout(
            getPendingTutorManifest(assignment.manifest, assignment.assignmentType),
            10000,
            'getPendingTutorManifest timeout after 10s'
          );
          if (tutorManifest && tutorManifest.length > 0) {
            const nextManifestItem = tutorManifest[0];

            // Check if this is a skillTutor type with special handling
            if (nextManifestItem.launchArgs && nextManifestItem.launchArgs.skill) {
              if (nextManifestItem.launchArgs.storyId && nextManifestItem.launchArgs.storyId !== '00000000-0000-0000-0000-000000000000') {
                // Has a valid storyId, use it
                forceStory = nextManifestItem.launchArgs.storyId;
                forceSkill = nextManifestItem.launchArgs.skill;
              } else {
                // No valid storyId but has skill, set forceSkill only
                forceSkill = nextManifestItem.launchArgs.skill;
                manifestItemId = nextManifestItem.manifestId; // Set manifestItemId so that we can save the storyId we get from story rec service back to manifest item
              }
            } else {
              // Regular tutor assignment types (tutor, ERSS, EPS, textSet)
              forceStory = nextManifestItem.storyId;
            }

            studentAssignment = {
              ...assignment,
              manifestItemId: manifestItemId
            };

            console.log("forcing tutor assignment story", forceStory, "skill:", forceSkill);
          }
        }

        if (session.userType === userTypes.STUDENT && !isRefresh) {
          this.props.history.push("/student/home");
        }
        this.setState({
          loading: false,
          forceStory: this.props.forceStory || forceStory, // Force story from url params first (props.forceStory), and then from the assignment if exists
          forceSkill: this.props.forceSkill || forceSkill, // Force skill from url params first, then from assignment
          studentAssignment: studentAssignment,
          isNonVerbalAssignment: isNonVerbalAssignment,
          // Dev mock AOS: use testlet flow even from tutor / no assessment assignment
          shouldUseAOS: isMockAOSNow(),
        })
      }
      this.setState({
        readyModal: true,
      }) // open ready modal, usually in loading state. // open a second time so that it can refresh the loading state in the modal UI
    } catch (error) {
      console.error("Error getting next assignment:", error);

      // If this is a refresh operation, rethrow the error for the caller to handle
      if (isRefresh) {
        throw error;
      }

      // Set loading to false and navigate to home if there's an error
      this.setState({
        loading: false
      });
      if (session.userType === userTypes.STUDENT) {
        // If coming from student inbox, redirect back to the inbox URL
        if (this.props.fromStudentInbox) {
          window.location.href = this.props.returnUrl;
        } else {
          // Default behavior for HMH users - go to main menu
          this.props.history.push("/student/home");
        }
      }
    } finally {
      // Every exit from here must classify the session, or the deferred ASR asset
      // preload would never start and the "Let's do it" gate would never open.
      // Assessment launches resolve to true above; everything else — including
      // fetch failures and empty manifests — lands here as tutor.
      if (!isLocalAsrSessionModeResolved()) {
        this.resolveLocalAsrSessionMode(false);
      }
    }
  }

  /**
   * Refreshes student assignments after completing an activity.
   * Does not navigate or update loading state - only refreshes assignment data.
   */
  async refreshAssignments() {
    const session = getUserData();
    const isRefresh = true;
    await this.checkAssignments(session, isRefresh);
  }

  skipNextReadingComprehension(){
    this.shouldSkipNextReadingComp = true;
  }

	// skipPastLastEnglish is a flag to skip past the last English assignment. This is used when AOS completes early, and we need to skip to spanish
  nextAssignment(skipPastLastEnglish = false){
    const {forcedAssessment, assignmentIndex} = this.state;

    var newAssignmentIndex = assignmentIndex + 1;
    if (skipPastLastEnglish) {
      newAssignmentIndex = this.getLastEnglishAssignmentIndex() + 1;
    }

    let currentAssignedActivity = forcedAssessment[assignmentIndex];
    let nextAssignedActivity = forcedAssessment[newAssignmentIndex];

    // Check if we should skip the next reading comprehension activity
    if (nextAssignedActivity && 
        nextAssignedActivity.type === assignmentTypes.READING_COMPREHENSION && 
        this.shouldSkipNextReadingComp) {
      // Skip the reading comprehension and move to the next assignment
      currentAssignedActivity = nextAssignedActivity; // Set currentAssignedActivity to the reading comprehension activity so that we can track sequence:BREAK properly
      newAssignmentIndex++;
      nextAssignedActivity = forcedAssessment[newAssignmentIndex];
    }

    this.shouldSkipNextReadingComp = false; // Reset after each nextAssignment. We only want to skip readingComp if it is the very next thing after being set

    // Check if AOS should be used for the next task
    let shouldUseAOS = false;
    if (FEATURE_FLAGS.AOS &&
        nextAssignedActivity &&
        nextAssignedActivity.locale &&
        (nextAssignedActivity.locale.toLowerCase() === 'en-us' ||
         nextAssignedActivity.locale.toLowerCase() === 'en_us')) {
      shouldUseAOS = true;
    }
    
    // Also allow AOS for calibration assignments regardless of locale
    // Note: assignmentType is on the assignment object, not the manifest item
    const assignmentType = this.props.studentAssignment ? this.props.studentAssignment.assignmentType : null;
    if (FEATURE_FLAGS.AOS &&
        assignmentType === ASSIGNMENT_TYPE.CALIBRATION) {
      shouldUseAOS = true;
    }

    // Spanish pilot uses AOS for all assessment tasks in the flow (including non-English manifest items)
    if (this.props.isSpanishPilot && nextAssignedActivity) {
      shouldUseAOS = true;
    }

    this.setState({
      assignmentIndex: newAssignmentIndex,
      bifurcatedAssignment: nextAssignedActivity && (currentAssignedActivity.sequence === ASSIGNMENT_SEQUENCE.BREAK),
      orfMode: nextAssignedActivity && nextAssignedActivity.type === assignmentTypes.ASSESSMENT,
      shouldUseAOS: shouldUseAOS || isMockAOSNow(),
    });
  }

  async handleStudentInboxTutor(session) {
    // Only reached when the inbox entry is explicitly not an assessment, so local ASR
    // can be released for this session without waiting on an assignment lookup.
    this.resolveLocalAsrSessionMode(false);

    // Check if user has tutor subscription access
    // But allow through if user is trying to access teacherMicTestMode or progress page
    const userData = getUserData();
    const { subscriptions, evaluarBTS } = userData;
    
    // Determine if user has required tutor subscription
    let hasRequiredSubscription = false;
    
    if (evaluarBTS) {
      // BTS enabled: strict locale-based check against the tutor the student launched.
      // Checked against the `tutorLocale` url param, not `userData.locale`: bilingual mode
      // forces the latter to `es-mx` so Amira speaks Spanish even for the English tutor.
      // English tutor requires `tutor`; Spanish tutor requires `tutora`.
      const tutorLocale = formatLocale_LowerCaseHyphen(this.props.tutorLocale);
      const requiredSubscription = tutorLocale === 'es-mx' ? 'tutora' : 'tutor';
      hasRequiredSubscription = subscriptions && subscriptions[requiredSubscription];
    } else {
      // BTS not enabled: permissive check - allow if tutor subscription is present for any language
      hasRequiredSubscription = subscriptions && subscriptions.tutor;
    }
    
    if (!hasRequiredSubscription && !this.props.teacherMicTest && !this.props.goToProgressPage) {
      window.location.href = this.props.returnUrl;
      return;
    }

    try {
      let anyCompleted = false;
      try {
        // Answered from the rows saveUserData already fetched; see hasAnyCompletedActivity.
        anyCompleted = await withTimeout(
          hasAnyCompletedActivity(session.userId),
          10000,
          'hasAnyCompletedActivity timeout after 10s'
        );
      } catch (error) {
        console.error("Error checking for any completed activities:", error);
        logSessionInfo("error_check_completed_activity", {error: error.message});
        // Move on without anyCompleted as false
      }
      const forceTutor = session.tutorEnabled && !anyCompleted && !session.isEarlyReader;
      setUserData('forceTutor', forceTutor);

      if (anyCompleted){
        StudentInformationService.setFTUESeen(session.userId, true)
        setUserData('hasSeenFTUE', true);
      }

      // Fetch current period info with timeout protection
      try {
        await withTimeout(
          this.fetchAndSetCurrentPeriodInfo(session),
          15000,
          'fetchAndSetCurrentPeriodInfo timeout after 15s'
        );
      } catch (error) {
        console.error("Error fetching current period info:", error);
        logSessionInfo("error_fetch_period_info", {error: error.message});
        // Continue without period info
      }

      if (session.userType === userTypes.STUDENT) {
        this.props.history.push("/student/home");
      }

      let newState = {
        loading: false,
        readyModal: true,
        forceStory: this.props.forceStory,
        forceSkill: this.props.skillId,
      };

      // Set studentAssignment on state if there is assignment data coming in through urlParams
      if (this.props.assignmentId && this.props.assignmentType) {
        newState.studentAssignment = {
          assignmentId: this.props.assignmentId,
          assignmentType: this.props.assignmentType,
          manifestItemId: this.props.manifestItemId,
        }
      }

      this.setState(newState);
    } catch (error) {
      logSessionInfo("error_handling_student_inbox_tutor", {error: error.message});
      window.location.href = this.props.returnUrl;
      return;
    }
  }

  getCoachMessage(){
    if(this.state.coachMessage){
      return this.state.coachMessage;
    } else if(this.state.pageIndex == 0){
      return ({id: Date.now(), text: "Let's get started!", texttoread: null});
    } else if(this.state.paginatedStory && this.state.paginatedStory.pages && (this.state.pageIndex >= this.state.paginatedStory.pages.length - 1)){
      return ({id: Date.now(), text: "Great job! You finished the story!", texttoread: "Great job! You finished the story"});
    } else {
      return null;
    }
  }

  coachReadyCallback(inState){
    this.setState({
      coachReady: inState,
      readyModal: true,
    });
  }

  setCoachMessage(message){
    this.setState({
      coachMessage: {
        text: message.text || message,
        pronunciation: message.pronunciation || message.text || message,
        callback: message.callback,
        id: this.state.coachMessage ? this.state.coachMessage.id + 1 : 0,
        skipAvatar: (!this.useCoach) || (!!message.skipAvatar),
        passThroughSpeechTags: message.passThroughSpeechTags ? true : false,
      },
    });
  }

  // for now this does nothing because we want students to be permanently stuck in forced assessment mode for MVP.
  // Once we have real assignments, and want to provide access to the rest of the student experience,
  // this function will reset the forcedAssessment state and will call an API function to mark the assignment as completed
  clearAssignment(){
    return null;
  }

  //Don't let ChatBot receive a transcript,
  //instead channel the transcript to ReaderManager
  unMuteReader(){
    this.setState({
      isReaderActive: true,
    });
  }

  //Channel the transcript to ChatBot
  muteReader(){
    this.setState({
      isReaderActive: false,
    });
  }

  // display menu only for tutor mode
  getMenu(speechProps){
     let renderMenu = () => {
      return (
        <div className="menuContainer">
          <Menu
            interventions={this.state.interventions}
            onOpen={speechProps ? speechProps.stopListening : null}
            onClose={speechProps ? speechProps.startListening : null}
          />
        </div>
      );
    };
    return(
      <Route exact path='/student/tutor' render={renderMenu} />
    );
  }

  // display nav only for tutor mode
  getNav(){
    let renderNav = () => {
      return (
        <div className="NavigationContainer">
          <Navigation next={this.nextPage} previous={this.previousPage}/>
        </div>
      );
    };
    return(
      <Route exact path='/student/tutor' render={renderNav} />
    );
  }

  renderStudentHome(routerProps, speechProps){
    //it's easier and cleaner to pass this info through as props at this point, for this piece
    let session = getUserData();

    return (
      <StudentHome
        resetStory={this.resetStory}
        closeModal={this.closeModal}
        openModal={this.openModal}
        sendMessage={this.setCoachMessage}
        speech={speechProps}
        studentName={session.firstName}
        studentId={session.userId}
        session={session}
        setLocale={this.setLocale.bind(this)}
        forceStory={this.state.forceStory}
        fromStudentInbox={this.props.fromStudentInbox}
        tutorLocale={this.props.tutorLocale}
        goToProgressPage={this.props.goToProgressPage}
        goToTutorPicker={this.props.goToTutorPicker}
        showTutorPickerButton={this.showTutorPickerButton}
        hideTutorPickerButton={this.hideTutorPickerButton}
        studentHomeSettingsVisible={this.state.studentHomeSettingsVisible}
        teacherMicTestMode={this.state.teacherMicTestMode}
        {...routerProps}
      />
    );
  }

  renderStudentHistory(routerProps,speechProps){
    const session = getUserData();
    return (
      <StudentHistory
        setLocale={this.setLocale.bind(this)}
        speech={speechProps}
        session={session}
        switchReaderContainerBackground={this.switchReaderContainerBackground}
        openModal={this.openModal}
        return='/student/home'
        fromStudentInbox={this.props.fromStudentInbox}
        returnToInboxURL={this.props.returnUrl}
        closeModal={this.closeModal}
        {...routerProps}
      />
    );
  }

  renderTutorPicker(routerProps, speechProps){
    const session = getUserData();
    return (
      <TutorPicker
        {...routerProps}
        speech={speechProps}
        session={session}
        fromStudentInbox={this.props.fromStudentInbox}
        returnToInboxURL={this.props.returnUrl}
        webglProps={this.props.webglProps} // passed in through appContainer wrapped in <WebGLContext>
      />
    );
  }

  renderStudentReports(routerProps){
    return (
      <NativeReports
        return='/student/home'
        {...routerProps}
      />
    );
  }

  onPreCheckDone(){
    if (this.state.preChecksDone) return;
    this.resetStory()
    this.setState({
      preChecksDone: true
    })
    this.props.setPreChecksDone(true);
  }

  getReader(routerProps, speechProps, transcript, readerConfig=this.props.config){
    //TODO: passing test through here is a hack, which will be solved when we solve the config propogation bug
    return (
      <CurrentStoryWordMetadataProvider>
        <ReaderManagerErrorBoundary openModal={this.openModal}>
        <ReaderManager
          key={"readerInstance" + this.state.readerKey}    //this will force a fresh new component when the story changes. This way we wont accidentally have any old kruft holding over from previous sessions
          asrStoryID={!this.state.preChecksDone ? routerProps.asrStoryId : ""}
          preChecksDone={this.state.preChecksDone}
          readerManagerRef={this.readerManagerRef}
          forceDialogFTUE={this.props.forceDialogFTUE}
          isTest={readerConfig.test}
          modal={this.state.modal}
          config={routerProps.config || readerConfig}
          story={this.state.paginatedStory}
          rawStory={this.state.rawStory}
          title={this.state.paginatedStory ? this.state.paginatedStory.title : null}
          author={this.state.paginatedStory ? this.state.paginatedStory.author : null}
          brand={this.state.paginatedStory ? this.state.paginatedStory.brand : null}
          questions={this.state.paginatedStory ? this.state.paginatedStory.questions : null}
          items={this.state.paginatedStory ? this.state.paginatedStory.items : null}
          sendMessage={this.setCoachMessage}
          muteReader={this.muteReader}
          unMuteReader={this.unMuteReader}
          speech={speechProps}
          isQuiet={speechProps.isQuiet}
          numPhonemes={speechProps.numPhonemes}
          abPhonemes={speechProps.abPhonemes}
          transcript={this.state.isReaderActive ? transcript : {}}
          isReaderActive={this.state.isReaderActive}
          setInterventions={this.setInterventions}
          setStory={this.setStory}
          resetForcedStory={this.resetForcedStory}
          setAssessmentStoryDirectly={this.setAssessmentStoryDirectly}
          resetStory={this.resetStory}
          coachIsReady={this.state.coachReady}
          pageIndex={this.state.pageIndex}
          openModal={this.openModal}
          closeModal={this.closeModal}
          goToNextPage = {this.nextPage}
          studentAssignment={this.state.studentAssignment}
          forcedAssessment = {this.state.forcedAssessment && this.state.forcedAssessment[this.state.assignmentIndex]}
          allAssignments={this.state.forcedAssessment}
          assignmentIndex={this.state.assignmentIndex}
          parentActivityId = {this.state.parentActivityId}
          setParentActivityId = {this.setParentActivityId}
          lastAssignment = {this.state.forcedAssessment && (this.state.assignmentIndex >= this.state.forcedAssessment.length - 1)}
          bifurcatedAssignment = {this.state.bifurcatedAssignment}
          firstAssignment = {this.state.assignmentIndex == 0}
          activityIndex = {this.state.activityIndex}
          clearAssignment = {this.clearAssignment}
          refreshAssignments = {this.refreshAssignments}
          selectStories = {this.props.selectStories}
          forceStory = {this.state.forceStory}
          forceSkill = {this.state.forceSkill}
          forceIntervention = {this.props.forceIntervention}
          interventionBias = {this.props.interventionBias}
          midAssessment = {this.state.midAssessment}
          setMidAssessment = {this.setMidAssessment}
          logTimeOnStoryStart={this.logTimeOnStoryStart}
          logTimeOnStoryEnd={this.logTimeOnStoryEnd}
          orfMode = {this.state.orfMode}
          nextAssignment = {this.nextAssignment}
          forceAssignment={this.forceAssignment}
          setLocale={this.setLocale.bind(this)}
          skipIntros = {this.props.skipIntros}
          forceInterventionSelectionGrade={this.props.forceInterventionSelectionGrade}
          forceInterventionSelectionWCPM={this.props.forceInterventionSelectionWCPM}
          forceInterventionSelectionARM={this.props.forceInterventionSelectionARM}
          returnUrl={this.props.returnUrl}
          amiraIsipPilotGuid={this.props.amiraIsipPilotGuid}
          spanishIsipCalibrationGuid={this.props.spanishIsipCalibrationGuid}
          isSpanishPilot={this.props.isSpanishPilot}
          fromStudentInbox={this.props.fromStudentInbox}
          // We make sure the prechecks are done before we use AOS, otherwise we are in the ASR story
          shouldUseAOS={this.state.shouldUseAOS && this.state.preChecksDone}
          shouldExitAfterAOS={this.shouldExitAfterAOS}
          period={this.state.period}
          schoolYear={this.state.schoolYear}
          onCampus={this.props.onCampus}
          getLastEnglishAssignmentIndex={this.getLastEnglishAssignmentIndex}
          setNextStory={this.setNextStory}
          skipNextReadingComprehension={this.skipNextReadingComprehension}
          isNonVerbalAssignment={this.state.isNonVerbalAssignment}
          initialUrlParams={this.initialUrlParams}
          startMidSessionTroubleshoot={this.startMidSessionTroubleshoot}
          {...routerProps}
        />
        </ReaderManagerErrorBoundary>
      </CurrentStoryWordMetadataProvider>
    );
  }

  // Methods to control the tutor picker button visibility
  showTutorPickerButton() {
    var brandProhibitsTutorSelection = GetBrandedProhibitTutorSelection();
    if (getUserData().tutorSelectionEnabled && !brandProhibitsTutorSelection) {
      this.setState({ tutorPickerButtonVisible: true });
    }
  }

  hideTutorPickerButton() {
    this.setState({ tutorPickerButtonVisible: false });
  }

  navigateToTutorPicker() {
    // Close the modal containing the VoicePicker before navigating
    this.closeModal();

    // Then navigate to the tutor picker
    this.props.history.push("/student/tutorPicker");
  }

  openStudentHomeSettingsModal() {
    this.setState(prevState => ({
      studentHomeSettingsVisible: !prevState.studentHomeSettingsVisible
    }));
  }

  navigateToMicTest() {    
    this.setState({
      teacherMicTestMode: true,
    }, () => {
        this.setState({ 
        ready: false,
        preChecksDone: false,
        hideReaderContainer: true,
        studentHomeSettingsVisible: false
      }, () => {
        this.props.history.push("/student/pre-checks");
      });
    });
  }

  // Method to reset teacher mic test mode flag
  resetTeacherMicTestMode() {
    this.setState({ teacherMicTestMode: false });
  }

  showMicMutedTroubleshootModal(evt) {
    if (this.studentPreCheckLogEventRef) { // if the log event callback is set, log the event
      this.studentPreCheckLogEventRef(evt?.detail?.type || 'TRACK_MUTED_DETECTED');
    }

    // to stop any ongoing speech or listening
    this.speechProps.stopListening();
    this.speechProps.abortSpeech();

    // Navigate to the mic-muted route to unmount all other student components
    this.props.history.push('/student/mic-issues');
    const kidErrorSpeech = getCopyByGradeband(COPY_STRINGS, 'kid_error_speech');
    this.speechProps.speakWithPromise({ textToSpeak: kidErrorSpeech });
    const micMutedErrorModal = (
      <Modal
        closeModal={null}
        delayedCloseButton={false}
        closeButton={false}
        backgroundClose={false}
        hideBackground={false}>
        <MutedTroubleshootModal handleRestart={this.handleRestart} />
      </Modal>
    );
    this.setState({
      error: true,
      audioInputErrorModal: micMutedErrorModal,
    });
  }

  handleRestart() {
    this.restartSession();
  }

  setLogEventCallback(logEventFn) { // set the log event callback for the student pre-checks
    this.studentPreCheckLogEventRef = logEventFn;
  }

  render() {
    //If the microphone is not enabled, all we can/should show is the modal
    // if(!this.state.micChecked) return(this.state.modal);

    const { userType, partner, branding } = getUserData();

    /*
    * REDIRECTS
    * Handle initial redirect if necessary.
    * Doing this here rather than on inner route render so that surrounding styles don't get lost on loading screen
    */

    //Evaluate these bools here to make the code more readable below.
    let redirectToActivities = false;
    let redirectToAssessment = false;
    let redirectToHome = false;

    // Use if-else so we don't get multiple redirects. The way it worked before this was returning the redirects
    // directly, so there was an implicit priority. We need to put the redirects down in the <Speech> structure
    // rather than directly returning to prevent the whole tree from being torn down and rebuilt.
    if(this.state.ready){
      if(userType == userTypes.TEACHER && !this.props.location.pathname.includes('teacher') && !getUserData().reportingRole){
        redirectToActivities = true;
      }else if(userType == userTypes.STUDENT && this.state.forcedAssessment && !this.props.location.pathname.includes('assessment') && !this.state.midSessionMicTroubleshoot){
        redirectToAssessment = true;
      }
    }else{
      if(userType == userTypes.STUDENT && !this.props.location.pathname.includes('student')){
        redirectToHome = true;
      }
    }
    /*******************************************************************/

    let isTeacher = this.props.location.pathname.includes('teacher') || this.props.location.pathname.includes('admin');
    let paperStyle = !isTeacher

    // Check if current route should hide coach
    const shouldHideCoach = this.routesThatHideCoach.some(route =>
      this.props.location.pathname.includes(route)
    );

    let readerContainerClasses = cx({
      'readerContainer': true,
      'reportBox' : !paperStyle,
      'fullScreen': !this.useCoach || shouldHideCoach
    });

    let readerClasses = cx({
      'reader': true,
      'paper' : paperStyle,
    });

    let pixiStageClass = cx({
      'appLevelPixiStage': true,
      'active': this.state.animationToPlay, // used to move the z index to put the animation on top
    });



      return (
        <Speech
          config={this.props.config}
          forceKaldi={!this.state.orfMode && userType == userTypes.STUDENT}
          skipAvatar={!this.useCoach}
          forceGooglePM={this.props.forceGooglePM}
          isSpanishPilot={this.props.isSpanishPilot}
          localAsrReady={this.state.localAsrReady}
        >
        {(speechProps, transcript) => {
          // Store speechProps on the instance so it's available to other methods
          this.speechProps = speechProps;
          
          return <Fragment>
            <div className="app">
              {/* Assessment In Progress Icon - shows in top right during forced assessment for NM state */}
              {shouldShowAssessmentInProgressIcon(this.state.forcedAssessment) && (
                <img
                  src={assessmentInProgressIcon}
                  alt="Assessment In Progress"
                  className="assessment-in-progress-icon"
                />
              )}
              {redirectToActivities &&
                <Route render={(props) => <Redirect to='/teacher/gradebook/activities' {...props} />} />
              }
              {redirectToAssessment &&
                <Route render={(props) => <Redirect to='/student/assessment' {...props} />} />
              }
              {redirectToHome &&
                <Route render={(props) => <Redirect to='/student/home' {...props} />} />
              }
              {this.state.readyModal && this.openReadyModal(speechProps)}
              {/*app level stage to display pixi animations that it's ok to remove from the tree, or pure pixi routes*/}
              {!shouldHideCoach && (<div className={"rightPanel" + (!this.useCoach ? " hidden" : "") + (this.props.webglError ? " webgl-error" : "")}>
                <Coach
                  config={this.props.config}
                  message={this.getCoachMessage()}
                  speech={speechProps}
                  muteReader={this.muteReader}
                  unMuteReader={this.unMuteReader}
                  isReaderActive={this.state.isReaderActive}
                  transcript={!this.state.isReaderActive ? transcript : {}}
                  isCoachReady={this.state.coachReady}
                  isAppReady={this.state.ready}
                  setCoachReady={this.coachReadyCallback}
                  setCoachMessage={this.setCoachMessage}
                  avatarSpeechProps={speechProps.avatarSpeechProps}
                  webglProps={this.props.webglProps} // passed in through appContainer wrapped in <WebGLContext>
                  webglError={this.props.webglError} // pass WebGL error state for fallback handling
                  isHidden={!this.useCoach}
                />
                {this.state.ready && this.state.coachReady && <SpeechButton speech={speechProps} />}
                {this.useMenu && this.getMenu(speechProps)}
                {this.useNav && this.getNav()}
              </div>)}

              {/* TT-1166: "thinking" bubble over Amira during slow TTS. Mounted here for
                  lifecycle only; it portals its output to document.body, so its position in
                  this tree does not affect layout. `visible` gates on avatar visibility;
                  `webglError` only selects the static-fallback anchor. */}
              <ThinkingBubble
                visible={this.useCoach && !shouldHideCoach && this.state.coachReady}
                webglError={this.props.webglError}
              />

              <div className={readerContainerClasses}>
                <div className={readerClasses}>
                  <Route path='/student/pre-checks/asr' render={ (props) => <StudentASRReaderWrapper history={this.props.history} initialUserInteraction={this.state.initialUserInteraction} config={this.props.config} getReader={(props) => this.getReader(props, speechProps, transcript)}/> }/>
                  <Route path='/student/pre-checks' render={ (props) => {
                    // Use state flag instead of URL parameter to avoid infinite re-renders
                    const teacherMicTestMode = this.state.teacherMicTestMode === true || this.props.teacherMicTest;
                    return <StudentPreChecks 
                      history={this.props.history} 
                      initialUserInteraction={this.state.initialUserInteraction} 
                      speechProps={speechProps} 
                      onPreCheckDone={this.onPreCheckDone} 
                      forcedAssessment={this.state.forcedAssessment}
                      readerManagerRef={this.readerManagerRef} 
                      toggleReaderContainer={this.toggleReaderContainer} 
                      readyModuleTestMode={ this.state.readyModuleTestMode} 
                      screenInvisible={this.state.hideReaderContainer} 
                      failureReturnUrl={this.props.returnUrl} 
                      returnUrl={this.props.returnUrl}
                      teacherMicTest={teacherMicTestMode}
                      resetTeacherMicTestMode={this.resetTeacherMicTestMode}
                      fromStudentInbox={this.props.fromStudentInbox}
                      isNonVerbalAssignment={this.state.isNonVerbalAssignment}
                      onLogEventReady={this.setLogEventCallback}
                      midSessionMicTroubleshoot={this.state.midSessionMicTroubleshoot}
                      audioIssueType={this.state.audioIssueType}
                      midSessionTroubleshootSeen={this.initialUrlParams?.midSessionTroubleshootSeen === 'true'}
                      onRestartSession={this.restartSession}
                      onRestartSessionSuppressingTroubleshoot={this.restartSessionSuppressingTroubleshoot}
                    />;
                  }} />
                  {/* Mic muted error modal must override any other modal */}
                  {this.state.audioInputErrorModal || this.state.modal}
                  {this.state.ready &&
                    <Fragment>
                      <ForcedAssessment forcedAssessment={this.state.forcedAssessment} userType={userType}>
                        <Route path='/student/assessment' render={(props) => this.getReader(props, speechProps, transcript, config.assessment)} />
                        {/* Allow mid-session mic troubleshoot to render without redirecting back to assessment */}
                        <Route path='/student/pre-checks' render={() => null} />
                        <Route render={(props) => <Redirect to='/student/assessment' {...props} />} />
                      </ForcedAssessment>
                      <StudentRoutes forcedAssessment={this.state.forcedAssessment} userType={userType}>
                        <Route path='/student/mic-issues' render={() => null} />
                        <Route path='/student/pre-checks' render={() => null} />
                        <Route path='/student/home' render={(props) => this.renderStudentHome(props, speechProps)} />
                        <Route path='/student/tutor' render={(props) => this.getReader(props, speechProps, transcript)} />
                        <Route path='/student/test' render={(props) => this.getReader(props, speechProps, transcript, config.studentTest)} />
                        <Route path='/student/assessment' render={(props) => this.getReader(props, speechProps, transcript, config.assessment)} />
                        <Route path='/student/readAloud' render={(props) => this.getReader(props, speechProps, null, config.readAloud)} />
                        <Route path='/student/reports/:reportType/:reportId' render={(props) => this.renderStudentHistory(props,speechProps)} />
                        <Route path='/student/tutorPicker' render={(props) => this.renderTutorPicker(props, speechProps)} />
                        <Route render={(props) => <Redirect to='/student/home' {...props} />} />
                      </StudentRoutes>
                      <TeacherRoutes userType={userType} openModal={this.openModal} closeModal={this.closeModal}/>
                      <SchoolAdminRoutes userType={userType} openModal={this.openModal} closeModal={this.closeModal} />
                      <DistrictAdminRoutes userType={userType} openModal={this.openModal} closeModal={this.closeModal} />
                    </Fragment>
                  }
                </div>
              </div>
              {userType !== userTypes.STUDENT && (!branding || branding.toUpperCase() !== "EPS") && <ChatSupport/>}
              {userType === userTypes.TEACHER && partner !== "HMH" && <EmailCaptureModal/>}
              {/* Gear Options Modal */}
              {this.state.studentHomeSettingsVisible && (
                <StudentHomeSettingsModal 
                  history={this.props.history}
                  onClose={() => this.setState({ studentHomeSettingsVisible: false })}
                  closeStudentHomeModal={() => this.closeModal()}
                  speech={speechProps}
                  navigateToMicTest={this.navigateToMicTest.bind(this)}
                />
              )}
              {/* Tutor Picker Button - positioned absolutely */}
              {this.state.tutorPickerButtonVisible && (
                <img
                  src={require("../images/gear.svg")}
                  alt="Tutor Picker"
                  className="tutor-picker-button"
                  onClick={this.openStudentHomeSettingsModal}
                />
              )}
            </div>
          </Fragment>;
        }}
      </Speech>
    );
  }
}

App.propTypes = {
  location: PropTypes.object.isRequired,
}

export default withRouter(App);
